import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Radio,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
  notification,
} from "antd";
import type { RadioChangeEvent, SelectProps, UploadFile } from "antd";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CAMPAIGN_TEMPLATES_OPTIONS,
  CAMPAIGN_TYPE_OPTIONS,
  ChannelKeyMap,
  RfmPeriod,
} from "../constants/labels";
import {
  TemplatesTypeEnum,
  CampaignTypeEnum,
  ChannelEnum,
  RFMPeriod,
  StatusEnum,
  TypeEnum,
  ModeEnum,
  EndScheduleEnum,
  WeekDay,
} from "../constants/enums";
import { brandMemberSegmentService } from "../services/brandMemberSegmentService";
import { useBrandStore } from "../store/brandStore";
import { brandRFMProfileService } from "../services/brandRFMProfileService";
import {
  CheckCircleFilled,
  CloseOutlined,
  DeleteFilled,
  DeleteOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  PlusOutlined,
  SearchOutlined,
  SyncOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useWatch } from "antd/es/form/Form";
import { VARS } from "../constants/variables";
import { RoutePaths } from "../routes/route";
import { UploadChangeParam } from "antd/es/upload";
import { Image as AntdImage } from "antd";
import DOMPurify from "dompurify";
import { htmlToWhatsappText, mapToEVouchers } from "../utils/convert";
import VoucherTable from "../components/common/VoucherTable";
import {
  CampaignDetails,
  EstimatedTotalMembersRequest,
  CampaignInfo,
  ExcludeMember,
} from "../models/campaign";
import { campaignService } from "../services/campaignService";
import { PostalDistricts } from "../models/postal";
import { DateRange } from "../models/time";
import {
  applyPastTimeToday,
  BlockRange,
  buildDisabledTime,
  parseBlockedRanges,
} from "../utils/date";
import { useQueryNavigator } from "../utils/navigate";
import { Voucher } from "../models/voucher";
import { EVoucher } from "../models/evoucher";
import { CampaignFormValues } from "../models/formValues";
import { helperService } from "../services/helperService";
import { billingService } from "../services/billingService";
import EditorQuill from "../components/common/EditorQuill";
import EmailTemplateEditor, {
  parseEmailTemplate,
  quillHtmlToInner,
  resolveQuillAlignments,
  reconstructEditableContent,
  ParsedEmailTemplate,
} from "../components/common/EmailTemplateEditor";
import RFMGeneratingModal from "../components/common/GeneratingDataModal";
import { getSmsCharCount, getSmsSegments, isGsmText } from "../constants/text";
import { MessageTemplateData } from "../models/messageTemplates";
import MessageTemplatesModal from "../components/common/MessageTemplatesModal";
import { renderMessage } from "../utils/renderMessage";
import PreviewMemberModal from "../components/common/PreviewMemberModal";
import ExcludeMemberPreviewModal from "../components/common/ExcludeMemberPreviewModal";
import ExcludeMemberUpload from "../components/common/ExcludeMemberUpload";
import BillingModal from "../components/common/BillingModal";
import { parseExcludeMemberFile } from "../utils/excludeMember";
import { getDefaultMessage } from "../constants/defaultMessages";
import { DEFAULT_CAMPAIGN_IMAGES } from "../constants/images";
import { getChannelCost } from "../constants/cost";

const { Title, Text } = Typography;

const FieldRow: React.FC<{
  label: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
}> = ({ label, required, children }) => (
  <Row gutter={16} align="top" style={{ marginBottom: 12 }}>
    <Col xs={24} md={10} lg={8}>
      <Text>
        {label} {required && <Text type="danger">*</Text>}
      </Text>
    </Col>
    <Col xs={24} md={14} lg={16}>
      {children}
    </Col>
  </Row>
);

const calculateDeliverySeconds = (
  memberCount: number,
  channel: ChannelEnum | null,
): number => {
  switch (channel) {
    case ChannelEnum.Sms:
      return Math.max(memberCount, 60);
    case ChannelEnum.WhatsAppStandard: {
      const daysNeeded = Math.max(1, Math.ceil(memberCount / 30));
      return daysNeeded * 24 * 60 * 60;
    }
    case ChannelEnum.WhatsAppPremium: {
      const daysNeeded = Math.max(1, Math.ceil(memberCount / 250));
      return daysNeeded * 24 * 60 * 60;
    }
    default:
      return 12 * 24 * 60 * 60;
  }
};

const CreateCampaign: React.FC = () => {
  // ======================================================
  // 📱 Device Detection
  // ======================================================
  const isMobileOrTablet = window.innerWidth <= 1024;

  // ======================================================
  // 📝 Form & Navigation
  // ======================================================
  const [form] = Form.useForm<CampaignFormValues>();
  const location = useLocation();
  const { brandId, campaignInfo, setCampaignInfo } = useBrandStore();
  const navigate = useNavigate();
  const navigateWithQuery = useQueryNavigator();
  const { Option } = Select;

  // ======================================================
  // 📊 UI State Management
  // ======================================================
  const [scheduleType, setScheduleType] = useState<number>(TypeEnum.Onetime);
  const [endScheduleType, setEndScheduleType] = useState<number>(
    EndScheduleEnum.OnSpecificDate,
  );
  const [targetMemberMode, setTargetMemberMode] = useState<number>(
    ModeEnum.Refresh,
  );
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [evoucherCustom, setEvoucherCustom] = useState(false);
  const [messageTemplatesOpen, setMessageTemplatesOpen] = useState(false);
  const [selectedMessageTemplates, setSelectedMessageTemplates] =
    useState<MessageTemplateData | null>(null);

  const [firstReloadMember, setFirstReloadMember] = useState(true);
  const [changeRFM, setChangeRFM] = useState(false);
  const [
    isModalWarningCreateCampaignVisible,
    setIsModalWarningCreateCampaignVisible,
  ] = useState(false);
  const [selectedCampaignChannel, setSelectedCampaignChannel] =
    useState<ChannelEnum | null>(null);
  const [link, setLink] = useState("");
  const [enableTrackingLink, setEnableTrackingLink] = useState<boolean>(true);
  const [listMessageTemplates, setListMessageTemplates] = useState<
    MessageTemplateData[]
  >([]);
  const [showLink, setShowLink] = useState(false);
  const [nameError, setNameError] = useState<string | null | undefined>(
    undefined,
  );
  const [selectedTemplate, setSelectedTemplate] = useState(
    TemplatesTypeEnum.Default,
  );
  const [inactiveDays, setInactiveDays] = useState(60);
  const [loading, setLoading] = useState(false);
  const [loadingRefreshMember, setLoadingRefreshMember] = useState(false);
  const [loadingVoucher, setLoadingVoucher] = useState(false);

  const [estimatedTotalMember, setEstimatedTotalMember] = useState(0);
  const [estimatedMember, setEstimatedMember] = useState([]);
  const [totalMembersError, setTotalMembersError] = useState<string | null>(
    null,
  );
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const openPreview = () => setIsPreviewOpen(true);
  const closePreview = () => setIsPreviewOpen(false);

  const [newCampaignId, setNewCampaignId] = useState(0);

  // ======================================================
  // 📧 Email Preview & Template
  // ======================================================
  const [emailPreviewHtml, setEmailPreviewHtml] = useState<string | null>(null);
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [parsedEmailTemplate, setParsedEmailTemplate] =
    useState<ParsedEmailTemplate | null>(null);
  const [rawEmailTemplate, setRawEmailTemplate] = useState<string | null>(null);
  const [emailFieldValues, setEmailFieldValues] = useState<
    Record<string, string>
  >({});

  // ======================================================
  // 💳 Billing Flow State
  // ======================================================
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);

  const [isCampaignSuccessModalOpen, setIsCampaignSuccessModalOpen] =
    useState(false);
  const [isCampaignFailedModalOpen, setIsCampaignFailedModalOpen] =
    useState(false);
  const [pendingPayload, setPendingPayload] = useState<CampaignDetails | null>(
    null,
  );

  const [postalFilter, setPostalFilter] = useState<string[]>([]);
  const [alreadyReload, setAlreadyReload] = useState(false);
  const [search, setSearch] = useState<string>("");
  const [selectedKey, setSelectedKey] = useState<React.Key | null>(null);
  const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null);
  const [selectedEVoucher, setSelectedEVoucher] = useState<EVoucher | null>(
    null,
  );

  const startOfToday = dayjs().startOf("day");
  const [dataPostalDistrict, setDataPostalDistrict] = useState<
    PostalDistricts[]
  >([]);
  const [listBlockDate, setListBlockDate] = useState<DateRange[]>([]);
  const [listVoucher, setListVoucher] = useState<Voucher[]>([]);
  const [listEVoucher, setListEVoucher] = useState<EVoucher[]>([]);

  const filteredVouchers = listVoucher.filter((v) =>
    v.Name?.toLowerCase().includes(search.toLowerCase()),
  );
  const filteredVEouchers = listEVoucher.filter((v) =>
    v.Name?.toLowerCase().includes(search.toLowerCase()),
  );

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [progressUpload, setProgressUpload] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [notif, contextHolder] = notification.useNotification();

  // ======================================================
  // 🚫 Exclude Targeted Member (blacklist upload)
  // ======================================================
  const [excludeFileList, setExcludeFileList] = useState<UploadFile[]>([]);
  const [excludeRows, setExcludeRows] = useState<Record<string, any>[]>([]);
  const [excludeMembers, setExcludeMembers] = useState<ExcludeMember[]>([]);
  const [excludeRawFile, setExcludeRawFile] = useState<File | null>(null);
  const [excludeParsing, setExcludeParsing] = useState(false);
  const [isExcludePreviewOpen, setIsExcludePreviewOpen] = useState(false);

  // ======================================================
  // 📂 Criteria & RFM Selections
  // ======================================================
  const criteriaProfileValue = useWatch("Profile", form);
  const periodValue = useWatch("Period", form);
  const segmentValue = useWatch("Segments", form);
  const frequencyValue = useWatch("Frequency", form);
  const scheduleEndTypeValue = Form.useWatch("ScheduleEndType", form);
  const maxOccurrencesValue = Form.useWatch("MaxOccurrences", form);
  const repeatDaysValue = Form.useWatch("RepeatDays", form);
  const repeatMonthValue = Form.useWatch("RepeatMonth", form);
  const endDateValue = Form.useWatch("EndDate", form);
  const hasCriteria =
    criteriaProfileValue !== null && criteriaProfileValue !== undefined;
  const hasPeriod = !!periodValue;
  const hasSegment = Array.isArray(segmentValue)
    ? segmentValue.length > 0
    : !!segmentValue;
  // ======================================================
  // 📑 RFM Options (Segments / Profiles / Postal Codes)
  // ======================================================
  const AllBrand = "All Brands";

  const [selectedPurchaseBrand, setSelectedPurchaseBrand] = useState<string[]>([
    AllBrand,
  ]);
  const [listPurchaseBrandFilter, setListPurchaseBrandFilter] = useState<any[]>(
    [],
  );
  const [listBrandMemberSegment, setListBrandMemberSegment] = useState<
    { Id: number; Name: string }[]
  >([]);
  const [listBrandRFMProfile, setListBrandRFMProfile] = useState<
    { Id: number; Name: string; IsActive: boolean }[]
  >([]);

  const SegmentOptions: SelectProps["options"] = useMemo(
    () => listBrandMemberSegment.map((s) => ({ label: s.Name, value: s.Id })),
    [listBrandMemberSegment],
  );

  const ProfileOptions: SelectProps["options"] = useMemo(
    () =>
      [...listBrandRFMProfile].map((s) => ({
        label: s.IsActive ? `${s.Name} (Currently Selected)` : s.Name,
        value: s.Id,
      })),
    [listBrandRFMProfile],
  );

  const selectedPostalSectors = useMemo<string[]>(() => {
    if (!Array.isArray(dataPostalDistrict) || !postalFilter?.length) return [];

    const idSet = new Set(postalFilter.map((v) => String(v)));

    const merged = dataPostalDistrict
      .filter((d) => idSet.has(String(d.Id)))
      .flatMap((d) => d.PostalSectors ?? []);

    return Array.from(new Set(merged));
  }, [postalFilter, dataPostalDistrict]);

  const PostalCodeOptions: SelectProps["options"] = useMemo(
    () =>
      dataPostalDistrict.map((s) => ({
        label: `${s.PostalSectors.join(", ")} – ${s.GeneralLocation}`,
        value: s.Id,
        search: `${s.PostalSectors.join(" ")} ${s.GeneralLocation}`,
      })),
    [dataPostalDistrict],
  );

  const handleChangePurchaseBrand = (values: string[]) => {
    onChangeRFM();
    if (
      values.includes(AllBrand) &&
      !selectedPurchaseBrand.includes(AllBrand)
    ) {
      setSelectedPurchaseBrand([AllBrand, ...listPurchaseBrandFilter]);
      return;
    }

    if (
      !values.includes(AllBrand) &&
      selectedPurchaseBrand.includes(AllBrand)
    ) {
      setSelectedPurchaseBrand([]);
      return;
    }

    const allSelected = listPurchaseBrandFilter.every((b) =>
      values.includes(b),
    );
    if (allSelected) {
      setSelectedPurchaseBrand([AllBrand, ...listPurchaseBrandFilter]);
      return;
    }
    setSelectedPurchaseBrand(values.filter((v) => v !== AllBrand));
  };

  useEffect(() => {
    if (!listPurchaseBrandFilter?.length) return;

    if (
      selectedPurchaseBrand.includes(AllBrand) &&
      selectedPurchaseBrand.length === 1
    ) {
      const updated = [AllBrand, ...listPurchaseBrandFilter];
      setSelectedPurchaseBrand(updated);
      form.setFieldsValue({ PurchaseBrand: updated });
    } else {
      form.setFieldsValue({ PurchaseBrand: selectedPurchaseBrand });
    }
  }, [selectedPurchaseBrand, listPurchaseBrandFilter]);

  useEffect(() => {
    if (selectedTemplate === TemplatesTypeEnum.Default) {
      const allSegmentValues: number[] = (SegmentOptions ?? [])
        .map((option) => option.value)
        .filter((value): value is number => typeof value === "number");
      form.setFieldsValue({
        Period: RFMPeriod.Lifetime,
        Segments: allSegmentValues,
        Profile: listBrandRFMProfile?.find((s) => s.IsActive)?.Id,
      });
    }
  }, [selectedTemplate, ProfileOptions]);

  // ======================================================
  // 💬 Message Handling
  // ====================================================
  const msg = Form.useWatch("Message", form) ?? "";
  const isEmptyHtml = (html?: string) => {
    if (!html) return true;

    const text = html
      .replace(/<br\s*\/?>/gi, "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, "")
      .trim();

    return text.length === 0;
  };
  const SHOW_REWARD_TYPES = [
    CampaignTypeEnum.Voucher,
    CampaignTypeEnum.VoucherPoint,
    CampaignTypeEnum.EVoucher,
  ];
  const campaignName = Form.useWatch("Name", form) ?? 0.0;
  const selectedType = Form.useWatch("Type", form) ?? 0.0;
  const voucherVars = ["{VoucherName}", "{VoucherExpiry}"];
  const evoucherVars = ["{EVoucherName}", "{EVoucherPrice}"];

  const filteredVars =
    selectedType === CampaignTypeEnum.EVoucher
      ? [...VARS.filter((v) => !voucherVars.includes(v)), ...evoucherVars]
      : selectedType === CampaignTypeEnum.Voucher ||
          selectedType === CampaignTypeEnum.VoucherPoint
        ? VARS
        : VARS.filter((v) => !voucherVars.includes(v));
  const isVoucher =
    selectedType === CampaignTypeEnum.Voucher ||
    selectedType === CampaignTypeEnum.VoucherPoint;
  const isEVoucher = selectedType === CampaignTypeEnum.EVoucher;

  const evoucherCTAText = Form.useWatch("CustomCopyText", form);

  const isFirstChange = useRef(true);
  const isMessageUserEdited = useRef(false);
  const emailQuillRefs = useRef<
    Map<string, import("../components/common/EditorQuill").EditorQuillRef>
  >(new Map());
  const activeEmailFieldRef = useRef<string | null>(null);
  const shortenLinkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const effectiveActionText =
    isEVoucher && evoucherCustom && evoucherCTAText
      ? evoucherCTAText
      : "View here";
  const effectiveCTAUrl = link;
  const additionalMessageText = `\n\n${effectiveActionText}:\n${effectiveCTAUrl}\n\nTo Unsub:\nhttps://getz.co/a/UNSUB`;
  const additionalMessageWhatsappText = `\n👉 Reply with *OK* to receive your membership login link\n👉 Reply with *STOP* to opt out of future WhatsApp messages`;
  let finalMessage = msg + (enableTrackingLink ? additionalMessageText : "");
  finalMessage = finalMessage.replace(/\r\n/g, "\n");

  const charCount = getSmsCharCount(finalMessage);

  const isUnicodeText = !isGsmText(finalMessage);
  const smsSegments = getSmsSegments(finalMessage, isUnicodeText);

  // const isUnicodeMessage = isUnicode(msg);
  // const MAX_LENGTH = isUnicodeMessage ? 70 : 160;

  const insertVar = (v: string) => {
    const voucherName = selectedVoucher?.Name ?? "";
    const voucherExpiry = selectedVoucher?.EndDateFormated ?? "";

    if (v === "{VoucherName}") {
      v = voucherName;
    }

    if (v === "{VoucherExpiry}") {
      v = voucherExpiry ? dayjs(voucherExpiry).format("DD MMM YYYY") : "";
    }

    if (v === "{EVoucherName}") {
      v = selectedEVoucher?.Name ?? "";
    }

    if (v === "{EVoucherPrice}") {
      v = selectedEVoucher?.Price?.toString() ?? "";
    }

    // Email: insert via Quill API at the last cursor position of the active field.
    // The onChange callback on the EditorQuill propagates the new value to
    // emailFieldValues and form.Message automatically — no manual state update needed.
    if (selectedCampaignChannel === ChannelEnum.Email) {
      const fieldName = activeEmailFieldRef.current;
      if (fieldName) {
        emailQuillRefs.current.get(fieldName)?.insertTextAtCursor(v);
      }
      return;
    }

    // SMS / WhatsApp Standard: manipulate the form.Message HTML string directly.
    const html = form.getFieldValue("Message") || "";
    const lower = html.toLowerCase();
    const closeIdx = lower.lastIndexOf("</p>");

    let next: string;

    if (closeIdx !== -1) {
      const before = html.slice(0, closeIdx);
      const after = html.slice(closeIdx);

      const needsSpace =
        before.length > 0 && !/[ \t\n]$/.test(before.slice(-1));
      const spacer = needsSpace ? " " : "";

      next = before + spacer + v + after;
    } else {
      const needsSpace = html.length > 0 && !/[ \t\n]$/.test(html);
      next = html + (needsSpace ? " " : "") + v;
    }

    form.setFieldsValue({ Message: next });
  };

  const resolveVoucherVars = (message: string): string => {
    if (!selectedVoucher || !message) return message;
    const name = selectedVoucher.Name ?? "";
    const expiry =
      selectedVoucher.EndDateFormated || selectedVoucher.IssuanceEndDate
        ? dayjs(
            selectedVoucher.EndDateFormated ?? selectedVoucher.IssuanceEndDate,
          ).format("DD MMM YYYY")
        : "";
    const tagsArray: string[] = Array.isArray(
      selectedVoucher.RedemptionOrderTagsDisplayName,
    )
      ? selectedVoucher.RedemptionOrderTagsDisplayName
      : [];
    const tags =
      selectedCampaignChannel === ChannelEnum.Sms
        ? tagsArray.map((t, i) => `${i + 1}. ${t}`).join("\n")
        : `<ol>${tagsArray.map((t) => `<li>${t}</li>`).join("")}</ol>`;
    return message
      .replace(/\{VoucherName\}/g, name)
      .replace(/\{VoucherExpiry\}/g, expiry)
      .replace(/\{RedemptionOrderTagsDisplayName\}/g, tags);
  };

  const resolveEVoucherVars = (message: string): string => {
    if (!message) return message;
    return message
      .replace(/\{EVoucherName\}/g, selectedEVoucher?.Name ?? "")
      .replace(/\{EVoucherPrice\}/g, selectedEVoucher?.Price?.toString() ?? "");
  };

  const applyDefaultMessage = () => {
    if (!selectedType || !selectedCampaignChannel) return;
    if (selectedCampaignChannel === ChannelEnum.WhatsAppPremium) return;

    if (selectedCampaignChannel === ChannelEnum.Email) {
      if (!parsedEmailTemplate) return;
      const initial: Record<string, string> = {};
      parsedEmailTemplate.forEach((block) => {
        if (block.type === "editable")
          initial[block.fieldName] = block.defaultContent;
        else if (block.type === "editable-group")
          block.fields.forEach(
            ({ fieldName, defaultContent }) =>
              (initial[fieldName] = defaultContent),
          );
      });
      setEmailFieldValues(initial);
      isMessageUserEdited.current = false;
      const firstEditable = parsedEmailTemplate.find(
        (b) => b.type === "editable",
      );
      if (firstEditable && firstEditable.type === "editable") {
        form.setFieldsValue({ Message: firstEditable.defaultContent });
      }
      return;
    }

    const defaultMsg = getDefaultMessage(selectedType, selectedCampaignChannel);
    if (!defaultMsg) return;
    isMessageUserEdited.current = false;
    let resolved = defaultMsg;
    if (selectedType === CampaignTypeEnum.EVoucher) {
      resolved = resolveEVoucherVars(resolved);
    } else if (
      selectedType === CampaignTypeEnum.Voucher ||
      selectedType === CampaignTypeEnum.VoucherPoint
    ) {
      resolved = resolveVoucherVars(resolved);
    }
    form.setFieldsValue({ Message: resolved });
  };

  // ======================================================
  // 📆 Campaign Details
  // ======================================================
  // NOTE: `Form.useWatch("CampaignDate")` was not re-rendering on change under
  // antd 5 + React 19, so the derived start/end + submit value stayed stale.
  // Drive it from local state synced via the DatePicker's onChange instead.
  const [campaignDate, setCampaignDate] = useState<dayjs.Dayjs | null>(
    dayjs().add(1, "day").startOf("day").hour(8).minute(0).second(0),
  );
  const cost = Form.useWatch("CostPerMember", form) ?? 0.0;
  const budget = Form.useWatch("Budget", form) ?? 0;

  // const maxReach = useMemo(
  //   () => (cost > 0 ? Math.floor(budget / cost) : 0),
  //   [budget, cost]
  // );
  const composedEmailPreview = useMemo(() => {
    if (!rawEmailTemplate || selectedCampaignChannel !== ChannelEnum.Email)
      return null;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(rawEmailTemplate, "text/html");

      // Banner image replacement + overlay/text removal when a campaign image is set
      if (previewUrl) {
        doc.querySelectorAll<HTMLImageElement>("img").forEach((img) => {
          const cls =
            img.getAttribute("class") ?? img.getAttribute("className") ?? "";
          if (cls.includes("banner-image")) img.src = previewUrl;
        });
        doc.querySelectorAll<HTMLElement>("div").forEach((div) => {
          const s = (div.getAttribute("style") ?? "").replace(/\s/g, "");
          if (s.includes("position:absolute") && s.includes("linear-gradient"))
            div.remove();
        });
        doc.querySelectorAll<HTMLElement>("div").forEach((div) => {
          if ((div.getAttribute("class") ?? "").includes("banner-text"))
            div.remove();
        });
      }

      // Inject edited field values
      doc
        .querySelectorAll<HTMLElement>('[data-section="editable"]')
        .forEach((el) => {
          const fieldName = el.getAttribute("data-field") ?? "Message";
          const raw = emailFieldValues[fieldName];
          if (raw === undefined) return; // not edited yet — keep template default
          const userHtml = resolveQuillAlignments(raw);
          const td = el.querySelector<HTMLElement>("td");
          if (td) {
            const originalTdHtml = td.innerHTML;
            td.innerHTML = originalTdHtml.includes("<table")
              ? reconstructEditableContent(userHtml, originalTdHtml)
              : userHtml;
          } else {
            el.innerHTML = quillHtmlToInner(userHtml, el.tagName);
          }
        });

      // Inject UTM short link into CTA button and fallback text link
      if (link) {
        doc.querySelectorAll<HTMLAnchorElement>("a").forEach((a) => {
          const text = a.textContent?.trim() ?? "";
          if (text.includes("View Reward Details")) {
            a.href = link;
          } else if (
            text.startsWith("http") &&
            a.closest("p")?.textContent?.includes("button above")
          ) {
            a.href = link;
            a.textContent = link;
          }
        });
      }

      return doc.documentElement.outerHTML;
    } catch {
      return null;
    }
  }, [
    rawEmailTemplate,
    emailFieldValues,
    selectedCampaignChannel,
    previewUrl,
    link,
  ]);

  //  Estimated
  const estimatedTotalCost = useMemo(() => {
    if (selectedCampaignChannel === ChannelEnum.Sms) {
      return estimatedTotalMember * cost * smsSegments;
    }
    return estimatedTotalMember * cost;
  }, [estimatedTotalMember, cost, selectedCampaignChannel, smsSegments]);

  useEffect(() => {
    if (!budget) {
      form.setFieldsValue({ Budget: Number(estimatedTotalCost.toFixed(2)) });
    }
  }, [estimatedTotalCost]);

  const maxReach = useMemo(() => {
    const budgetFinal = budget ?? 0;

    if (selectedCampaignChannel === ChannelEnum.Sms) {
      const realCostPerMember = cost * smsSegments;
      return realCostPerMember > 0
        ? Math.floor(budgetFinal / realCostPerMember)
        : 0;
    }

    return cost > 0 ? Math.floor(budgetFinal / cost) : 0;
  }, [budget, cost, smsSegments, selectedCampaignChannel]);

  const calculateCampaignEndDate = (
    startDate: string | dayjs.Dayjs,
    memberCount: number,
    channel: ChannelEnum | null,
    recurring?: {
      scheduleEndType?: number;
      endDate?: dayjs.Dayjs;
      maxOccurrences?: number;
      repeatDays?: number[];
      repeatMonth?: number;
      frequency?: number;
    },
  ) => {
    const start = dayjs(startDate);

    const deliveryMs = calculateDeliverySeconds(memberCount, channel) * 1000;
    const deliveryEnd = start.add(deliveryMs, "millisecond");

    if (recurring) {
      const {
        scheduleEndType,
        endDate,
        maxOccurrences,
        repeatDays,
        repeatMonth,
        frequency,
      } = recurring;

      if (scheduleEndType === EndScheduleEnum.OnSpecificDate && endDate) {
        return dayjs(endDate)
          .add(deliveryMs, "millisecond")
          .format("YYYY-MM-DD HH:mm");
      }

      if (
        scheduleEndType === EndScheduleEnum.AfterOccurrences &&
        maxOccurrences
      ) {
        if (frequency === TypeEnum.Recurring_Monthly) {
          const lastRunStart = start.add(
            (maxOccurrences - 1) * (repeatMonth ?? 1),
            "month",
          );
          return lastRunStart
            .add(deliveryMs, "millisecond")
            .format("YYYY-MM-DD HH:mm");
        }
        if (frequency === TypeEnum.Recurring_Weekly) {
          const daysPerWeek = repeatDays?.length || 1;
          const lastRunWeek = Math.floor((maxOccurrences - 1) / daysPerWeek);
          const lastRunStart = start.add(lastRunWeek, "week");
          return lastRunStart
            .add(deliveryMs, "millisecond")
            .format("YYYY-MM-DD HH:mm");
        }
      }

      return start.add(1, "year").format("YYYY-MM-DD HH:mm");
    }

    return deliveryEnd.format("YYYY-MM-DD HH:mm");
  };

  const { formattedStart, formattedEnd } = useMemo(() => {
    if (!campaignDate) {
      return { formattedStart: "", formattedEnd: "" };
    }

    return {
      formattedStart: dayjs(campaignDate).format("YYYY-MM-DD HH:mm"),
      formattedEnd: calculateCampaignEndDate(
        campaignDate,
        estimatedTotalMember,
        selectedCampaignChannel,
        scheduleType !== TypeEnum.Onetime
          ? {
              scheduleEndType: scheduleEndTypeValue,
              endDate: endDateValue,
              maxOccurrences: maxOccurrencesValue,
              repeatDays: repeatDaysValue,
              repeatMonth: repeatMonthValue,
              frequency: frequencyValue,
            }
          : undefined,
      ),
    };
  }, [
    campaignDate,
    estimatedTotalMember,
    selectedCampaignChannel,
    scheduleType,
    scheduleEndTypeValue,
    endDateValue,
    maxOccurrencesValue,
    repeatDaysValue,
    repeatMonthValue,
    frequencyValue,
  ]);

  // ======================================================
  // 🔄 Member Refresh Logic
  // ======================================================

  const onChangeRFM = () => {
    if (!firstReloadMember) {
      setChangeRFM(true);
    }
    setAlreadyReload(false);
  };
  const postalTotal = useMemo(
    () => PostalCodeOptions.filter((o) => o.value !== "all").length,
    [PostalCodeOptions],
  );

  const handleRefreshMember = async () => {
    setLoadingRefreshMember(true);

    try {
      if (firstReloadMember) {
        setFirstReloadMember(false);
      }
      const data: EstimatedTotalMembersRequest = {
        BrandId: brandId ?? "",
        RFMProfile: criteriaProfileValue ?? 0,
        RFMPeriod: periodValue ?? 0,
        SegmentIds: segmentValue ?? [],
        IncludeListMember: true,
        FromAge: form.getFieldValue("ageRange")?.from,
        ToAge: form.getFieldValue("ageRange")?.to,
        PostCode: selectedPostalSectors,
        PurchaseBrand: selectedPurchaseBrand.includes(AllBrand)
          ? null
          : selectedPurchaseBrand,
        CampaignType: selectedType,
        TemplateType: selectedTemplate,
        InactiveDays:
          selectedTemplate === TemplatesTypeEnum.Default ? inactiveDays : 0,
      };
      const result = await campaignService.estimatedTotalMember(
        data,
        excludeRawFile ?? undefined,
      );
      setEstimatedTotalMember(result.EstimatedCountMember);
      setEstimatedMember(result.ListMember);
      setTotalMembersError(null);
      setChangeRFM(false);
      setAlreadyReload(true);
      return result;
    } catch (error) {
      console.error("Failed to refresh member:", error);
      return null;
    } finally {
      setLoadingRefreshMember(false);
    }
  };

  // ======================================================
  //  Schedule Type Switching
  // ======================================================
  const onScheduleChange = (e: RadioChangeEvent) => {
    const val = e.target.value;
    setScheduleType(val);
  };

  const onModeChange = (e: RadioChangeEvent) => {
    const val = e.target.value;
    setTargetMemberMode(val);
  };

  // ======================================================
  //  Recurring
  // ======================================================
  const onEndScheduleChange = (e: RadioChangeEvent) => {
    const val = e.target.value;
    setEndScheduleType(val);
  };
  // ======================================================
  //  Form Submission
  // ======================================================
  const handleSubmit = async (values: CampaignFormValues) => {
    try {
      const estimated = await handleRefreshMember();
      if (!estimated) return;
      // const nextEstimatedCost =
      //   selectedCampaignChannel === ChannelEnum.Sms
      //     ? estimated.EstimatedCountMember * cost * smsSegments
      //     : estimated.EstimatedCountMember * cost;

      const formattedCampaignEndDate = calculateCampaignEndDate(
        formattedStart,
        estimated.EstimatedCountMember,
        selectedCampaignChannel,
        scheduleType !== TypeEnum.Onetime
          ? {
              scheduleEndType: values.ScheduleEndType,
              endDate: values.EndDate,
              maxOccurrences: values.MaxOccurrences,
              repeatDays: values.RepeatDays,
              repeatMonth: values.RepeatMonth,
              frequency: values.Frequency,
            }
          : undefined,
      );

      const resolvedCampaignName = values.Name.trim();
      const resolvedCustomUrl =
        isEVoucher && evoucherCustom ? values.CustomUrl?.trim() : undefined;
      let resolvedLink = link;
      if (
        campaignInfo?.MemberPortalUrl &&
        resolvedCampaignName &&
        selectedCampaignChannel
      ) {
        const generatedLink = `${campaignInfo.MemberPortalUrl}?utm_source=${ChannelKeyMap[selectedCampaignChannel]}&utm_campaign=${resolvedCampaignName}&utm_medium=MemberPortal${resolvedCustomUrl ? `&targetedUrl=${encodeURIComponent(resolvedCustomUrl)}` : ""}`;
        resolvedLink = (await fetchShortenLink(generatedLink)) ?? link;
      }

      const shouldAppendTrackingText =
        enableTrackingLink &&
        // (selectedCampaignChannel === ChannelEnum.WhatsAppStandard ||
        selectedCampaignChannel === ChannelEnum.Sms;

      const resolvedAdditionalMessageText = `\n\n${effectiveActionText}:\n${resolvedLink}\n\nTo Unsub:\nhttps://getz.co/a/UNSUB`;
      const trackingText = shouldAppendTrackingText
        ? resolvedAdditionalMessageText
        : selectedCampaignChannel === ChannelEnum.WhatsAppStandard
          ? additionalMessageWhatsappText
          : "";

      if (estimated.EstimatedCountMember === 0) {
        setTotalMembersError("No members matched the selected criteria.");
        form.scrollToField("Name" as any, {
          behavior: "smooth",
          block: "start",
        });
        return;
      }
      const payload: CampaignDetails = {
        Name: values.Name,
        Type: values.Type,
        RFMPeriod: values.Period,
        FromAge: values.ageRange?.from,
        ToAge: values.ageRange?.to,
        Messages: [
          {
            Content:
              selectedCampaignChannel === ChannelEnum.WhatsAppStandard
                ? htmlToWhatsappText(values?.Message ?? "") +
                  "\n" +
                  trackingText
                : selectedCampaignChannel === ChannelEnum.WhatsAppPremium
                  ? (selectedMessageTemplates?.Content ?? "")
                  : selectedCampaignChannel === ChannelEnum.Sms
                    ? (values?.Message ?? "") + trackingText
                    : selectedCampaignChannel === ChannelEnum.Email
                      ? (composedEmailPreview ?? values?.Message ?? "")
                      : (values?.Message ?? ""),
            Link: resolvedLink,
            CampaignTemplates:
              selectedCampaignChannel === ChannelEnum.WhatsAppPremium
                ? selectedMessageTemplates
                : null,
            CustomCopyText:
              isEVoucher && evoucherCustom
                ? (values.CustomCopyText ?? undefined)
                : undefined,
            CustomUrl:
              isEVoucher && evoucherCustom
                ? (values.CustomUrl ?? undefined)
                : undefined,
            MessageSubject:
              selectedCampaignChannel === ChannelEnum.Email
                ? values.MessageSubject
                : undefined,
            MessagePreheader:
              selectedCampaignChannel === ChannelEnum.Email
                ? values.MessagePreheader
                : undefined,
          },
        ],

        Segments: (values.Segments ?? []).map((id) => ({ SegmentId: id })),
        PostalDistricts: (values.PostalCode ?? []).map((id) => ({
          DistrictId: Number(id),
        })),
        RFMProfile: values.Profile,
        BrandId: brandId ?? undefined,
        Status: values.Status,
        VoucherId:
          selectedType === CampaignTypeEnum.VoucherPoint ||
          selectedType === CampaignTypeEnum.Voucher
            ? selectedVoucher?.Id
            : undefined,
        SKU: selectedEVoucher?.SKU ?? undefined,
        CampaignStart: formattedStart,
        CampaignEnd: formattedCampaignEndDate,
        Budget: values.Budget,
        EstimatedTotalMembers: estimated.EstimatedCountMember,
        ScheduleType:
          scheduleType === TypeEnum.Onetime
            ? TypeEnum.Onetime
            : values.Frequency,
        ImageUrl: previewUrl
          ? previewUrl.startsWith("/")
            ? `${window.location.origin}${previewUrl}`
            : previewUrl
          : undefined,
        Channel: selectedCampaignChannel ?? undefined,
        PurchaseBrand: selectedPurchaseBrand.includes(AllBrand)
          ? null
          : JSON.stringify(selectedPurchaseBrand),
        ExcludeMembers: null,
        TemplateType: selectedTemplate,
        InactiveDays:
          selectedTemplate === TemplatesTypeEnum.Default ? inactiveDays : 0,
        Mode:
          scheduleType !== TypeEnum.Onetime
            ? ModeEnum.Refresh
            : targetMemberMode,
        Retargeting:
          scheduleType !== TypeEnum.Onetime ? values.Retargeting : undefined,
        IsBilled: 0,
        ScheduleEndType:
          values.Frequency !== TypeEnum.Onetime
            ? values.ScheduleEndType
            : undefined,
        RepeatMonth:
          values.Frequency !== TypeEnum.Onetime
            ? values.RepeatMonth
            : undefined,
        RepeatDays:
          values.Frequency === TypeEnum.Recurring_Weekly
            ? JSON.stringify(values.RepeatDays)
            : undefined,
        EndDate:
          values.ScheduleEndType === EndScheduleEnum.OnSpecificDate
            ? dayjs(values.EndDate).format("YYYY-MM-DD HH:mm")
            : undefined,
        MaxOccurrences:
          values.ScheduleEndType === EndScheduleEnum.AfterOccurrences
            ? values.MaxOccurrences
            : undefined,
        EstimatedIntervalTime: calculateDeliverySeconds(
          estimated.EstimatedCountMember,
          selectedCampaignChannel,
        ),
        EnableCustomTrackingLink: isEVoucher ? evoucherCustom : undefined,
      };
      setPendingPayload(payload);
      setIsBillingModalOpen(true);
      // if (payload.Status === StatusEnum.Draft) {
      //   // Draft — skip billing, create directly
      //   setLoading(true);
      //   try {
      //     const resultId = await campaignService.createCampaignDetails(payload);
      //     if (!resultId) {
      //       setIsCampaignFailedModalOpen(true);
      //       return;
      //     }
      //     setNewCampaignId(resultId);

      //     setIsCampaignSuccessModalOpen(true);
      //   } catch {
      //     setIsCampaignFailedModalOpen(true);
      //   } finally {
      //     setLoading(false);
      //   }
      // } else {
      //   // setPendingPayload(payload);
      //   setLoading(true);
      //   try {
      //     const resultId = await campaignService.createCampaignDetails(
      //       payload!,
      //     );
      //     setNewCampaignId(resultId);
      //     if (!resultId) {
      //       alert("Failed to create campaign.");
      //       return;
      //     } else {
      //       setIsCampaignSuccessModalOpen(true);
      //     }
      //     // navigate(
      //     //   `${RoutePaths.IFRAME_DETAIL_CAMPAIGN}${location.search}&id=${resultId}`,
      //     // );
      //   } catch {
      //     setIsCampaignFailedModalOpen(true);
      //   } finally {
      //     setLoading(false);
      //   }
      // }
    } catch (err) {
      console.error(err);
      return Promise.reject(err);
    }
  };

  // Helper
  const isChannelEnum = (v: unknown): v is ChannelEnum =>
    typeof v === "number" && v in ChannelEnum;

  // ======================================================
  // 🗂 Data Fetching
  // ======================================================
  const fetchListAccountBrand = async () => {
    if (brandId) {
      try {
        setLoadingVoucher(true);
        const data = await campaignService.getListAccountBrand(brandId);
        if (data) {
          setListPurchaseBrandFilter(
            Array.from(
              new Set(
                data
                  .map((s: any) => s.BrandName?.trim())
                  .filter((name: any) => name && name !== ""),
              ),
            ).sort((a: any, b: any) => a.localeCompare(b)),
          );
        }
      } catch (error) {
        console.error("Error fetching account brands:", error);
      } finally {
        setLoadingVoucher(false);
      }
    }
  };

  const fetchVoucherList = async () => {
    if (brandId) {
      try {
        setLoadingVoucher(true);
        const data = await campaignService.getAllVouchers();
        if (data) {
          setListVoucher(data);
        }
      } catch (error) {
        console.error("Error fetching vouchers:", error);
      } finally {
        setLoadingVoucher(false);
      }
    }
  };

  const fetchEVoucherList = async () => {
    if (brandId) {
      try {
        setLoadingVoucher(true);
        const isStaging = process.env.REACT_APP_API_URL?.includes("sit");
        let emailParam = campaignInfo?.Email ?? "";

        if (isStaging) {
          const creditCards = await billingService.getCreditCard();
          const emails = creditCards?.map((c) => c.email).filter(Boolean) ?? [];
          if (emails.length > 0) emailParam = emails.join(",");
        }

        const data = await campaignService.getAllEVouchers(emailParam);
        if (data) {
          const mapped = mapToEVouchers(data);
          setListEVoucher(mapped);
        }
      } catch (error) {
        console.error("Error fetching E-Voucher:", error);
      } finally {
        setLoadingVoucher(false);
      }
    }
  };

  const fetchBlockDate = async () => {
    if (brandId && selectedCampaignChannel) {
      try {
        const data = await campaignService.getBlockedCampaignDateRanges(
          brandId,
          selectedCampaignChannel,
        );
        if (data) {
          setListBlockDate(data);
        }
      } catch (error) {
        console.error("Error fetching block dates:", error);
      }
    }
  };

  const fetchPostalDistrict = async () => {
    if (brandId) {
      try {
        const data = await campaignService.getAllPostalDistrict();
        if (data) {
          setDataPostalDistrict(data);
        }
      } catch (error) {
        console.error("Error fetching postal district:", error);
      }
    }
  };

  const fetchBrandMemberSegment = async () => {
    if (brandId) {
      try {
        const data =
          await brandMemberSegmentService.listBrandMemberSegment(brandId);
        if (data) {
          setListBrandMemberSegment(data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  const fetchBrandRFMProfile = async () => {
    if (brandId) {
      try {
        const data = await brandRFMProfileService.getBrandRFMProfile(brandId);
        if (data) {
          setListBrandRFMProfile(data);
          form.setFieldsValue({ Profile: data.find((s) => s.IsActive)?.Id });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  const fetchLinkOS = async () => {
    if (brandId) {
      try {
        await helperService.getPrimaryDomainByBrandId(brandId);
      } catch (error) {
        console.error("Error get link OS:", error);
      }
    }
  };

  const FetchCheckAvailableCampaignName = async (value: string) => {
    if (brandId) {
      try {
        const data = await campaignService.checkAvailableCampaignName(
          value,
          undefined,
        );
        return data;
      } catch (error) {
        console.error("Error Check Available Campaign Name:", error);
      }
    }
  };

  const fetchShortenLink = async (value: string): Promise<string | null> => {
    if (brandId) {
      try {
        const data = await helperService.getShortenLink(value);
        if (data) {
          setLink(data);
          setShowLink(true);
          return data;
        }
      } catch (error) {
        console.error("Error get shorten link:", error);
      }
    }
    return null;
  };

  const fetchMessageTemplates = async () => {
    if (brandId) {
      try {
        const data = await campaignService.getAllMessageTemplates();
        if (data) {
          setListMessageTemplates(data);
        }
      } catch (error) {
        console.error("Error fetch message templates:", error);
      }
    }
  };

  const handleOpenEmailPreview = async () => {
    if (!selectedType || !brandId) return;
    setEmailPreviewLoading(true);
    setEmailPreviewOpen(true);
    const isVoucherType =
      selectedType === CampaignTypeEnum.Voucher ||
      selectedType === CampaignTypeEnum.VoucherPoint;
    const rawHtml = await campaignService.getEmailPreview(
      selectedType,
      brandId,
      isVoucherType ? selectedVoucher?.Id : undefined,
    );
    if (rawHtml) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(rawHtml, "text/html");
      doc
        .querySelectorAll<HTMLElement>('[data-section="editable"]')
        .forEach((el) => {
          const fieldName = el.getAttribute("data-field") ?? "Message";
          const userHtml = emailFieldValues[fieldName] ?? "";
          const td = el.querySelector<HTMLElement>("td");
          const target = td ?? el;
          if (td) {
            target.innerHTML = userHtml;
          } else {
            target.innerHTML = quillHtmlToInner(userHtml, el.tagName);
          }
          target.style.outline = "2px dashed #1677ff";
          target.style.outlineOffset = "4px";
          target.style.position = "relative";
          const badge = doc.createElement("div");
          badge.style.cssText =
            "position:absolute;top:4px;right:4px;background:#1677ff;color:#fff;font-size:10px;padding:2px 6px;border-radius:4px;font-family:Arial,sans-serif;z-index:10;";
          badge.textContent = "✏ Message Content";
          target.appendChild(badge);
        });
      if (link) {
        doc.querySelectorAll<HTMLAnchorElement>("a").forEach((a) => {
          const text = a.textContent?.trim() ?? "";
          if (text.includes("View Reward Details")) {
            a.href = link;
          } else if (
            text.startsWith("http") &&
            a.closest("p")?.textContent?.includes("button above")
          ) {
            a.href = link;
            a.textContent = link;
          }
        });
      }
      setEmailPreviewHtml(doc.documentElement.outerHTML);
    } else {
      setEmailPreviewHtml(null);
    }
    setEmailPreviewLoading(false);
  };

  // ======================================================
  // ⚛️ Effects
  // ======================================================

  useEffect(() => {
    if (campaignInfo?.Email) {
      fetchEVoucherList();
    }
  }, [campaignInfo]);

  useEffect(() => {
    if (!brandId || selectedCampaignChannel !== ChannelEnum.Email) {
      setParsedEmailTemplate(null);
      setRawEmailTemplate(null);
      return;
    }
    const isVoucherType =
      selectedType === CampaignTypeEnum.Voucher ||
      selectedType === CampaignTypeEnum.VoucherPoint;
    campaignService
      .getEmailPreview(
        selectedType || undefined,
        brandId,
        isVoucherType ? selectedVoucher?.Id : undefined,
      )
      .then((rawHtml) => {
        setRawEmailTemplate(rawHtml);
        setParsedEmailTemplate(
          rawHtml ? (parseEmailTemplate(rawHtml) ?? null) : null,
        );
      });
  }, [selectedType, selectedVoucher?.Id, brandId, selectedCampaignChannel]);

  useEffect(() => {
    if (!parsedEmailTemplate) {
      setEmailFieldValues({});
      return;
    }
    const initial: Record<string, string> = {};
    parsedEmailTemplate.forEach((block) => {
      if (block.type === "editable") {
        initial[block.fieldName] = block.defaultContent;
      }
    });
    setEmailFieldValues(initial);
    const firstEditable = parsedEmailTemplate.find(
      (b) => b.type === "editable",
    );
    if (firstEditable && firstEditable.type === "editable") {
      form.setFieldsValue({ Message: firstEditable.defaultContent });
    }
  }, [parsedEmailTemplate]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlChannel = urlParams.get("campaign_channel");

    const parsed = urlChannel ? parseInt(urlChannel, 10) : NaN;
    const finalValue = isChannelEnum(parsed) ? parsed : ChannelEnum.Sms;

    setSelectedCampaignChannel(finalValue);

    form.setFieldsValue({
      CostPerMember: getChannelCost(finalValue),
    });

    // if (urlChannel !== null) {
    //   urlParams.delete("campaign_channel");

    //   const newUrl =
    //     window.location.pathname +
    //     (urlParams.toString() ? `?${urlParams.toString()}` : "");

    //   window.history.replaceState({}, "", newUrl);
    // }
  }, [location.search]);

  useEffect(() => {
    if (brandId && !campaignInfo) {
      campaignService.getCampaignInfo(brandId).then((info) => {
        if (info) setCampaignInfo(info);
      });
    }

    const timeout = setTimeout(async () => {
      if (brandId && selectedCampaignChannel) {
        await fetchListAccountBrand();
        await fetchBrandMemberSegment();
        await fetchBrandRFMProfile();
        await fetchPostalDistrict();
        await fetchBlockDate();
        await fetchVoucherList();
        await fetchLinkOS();
        // await fetchMessageTemplates();
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [brandId, selectedCampaignChannel]);

  useEffect(() => {
    if (nameError === null) {
      form.validateFields(["Name"]);
    }
  }, [nameError]);

  // useEffect(() => {
  //   const tomorrow8 = dayjs()
  //     .add(1, "day")
  //     .startOf("day")
  //     .hour(8)
  //     .minute(0)
  //     .second(0);

  //   const current = form.getFieldValue("CampaignDate");
  //   if (current) {
  //     const cur = dayjs(current);
  //     const isAfterMin = !cur.isBefore(tomorrow8);
  //     const isCurrentBlocked = blocked.some(
  //       (r) => !cur.isBefore(r.start) && !cur.isAfter(r.end),
  //     );
  //     if (isAfterMin && !isCurrentBlocked) return;
  //   }

  //   let candidate = tomorrow8;
  //   for (let i = 0; i < 365; i++) {
  //     const isBlocked = blocked.some(
  //       (r) => !candidate.isBefore(r.start) && !candidate.isAfter(r.end),
  //     );
  //     if (!isBlocked) {
  //       form.setFieldsValue({ CampaignDate: candidate });
  //       return;
  //     }
  //     candidate = candidate
  //       .add(1, "day")
  //       .startOf("day")
  //       .hour(8)
  //       .minute(0)
  //       .second(0);
  //   }
  // }, [listBlockDate]);

  useEffect(() => {
    if (SHOW_REWARD_TYPES.includes(selectedType))
      form.validateFields(["Voucher"]);
  }, [selectedVoucher, selectedEVoucher]);

  useEffect(() => {
    if (!selectedType || !selectedCampaignChannel) return;
    if (selectedCampaignChannel === ChannelEnum.WhatsAppPremium) return;
    if (isMessageUserEdited.current) return;
    const defaultMsg = getDefaultMessage(selectedType, selectedCampaignChannel);
    if (defaultMsg)
      form.setFieldsValue({
        Message: resolveEVoucherVars(resolveVoucherVars(defaultMsg)),
      });
  }, [selectedType]);

  useEffect(() => {
    if (!selectedEVoucher) return;
    if (isMessageUserEdited.current) {
      const currentMsg = form.getFieldValue("Message");
      if (!currentMsg) return;
      const resolved = resolveEVoucherVars(currentMsg);
      if (resolved !== currentMsg) form.setFieldsValue({ Message: resolved });
      return;
    }
    if (!selectedType || !selectedCampaignChannel) return;
    if (selectedCampaignChannel === ChannelEnum.WhatsAppPremium) return;
    const defaultMsg = getDefaultMessage(selectedType, selectedCampaignChannel);
    if (defaultMsg)
      form.setFieldsValue({
        Message: resolveEVoucherVars(resolveVoucherVars(defaultMsg)),
      });
  }, [selectedEVoucher]);

  useEffect(() => {
    if (
      (selectedCampaignChannel === ChannelEnum.WhatsAppStandard ||
        selectedCampaignChannel === ChannelEnum.WhatsAppPremium) &&
      selectedType
    ) {
      const isDefaultImage =
        fileList.length === 0 ||
        (fileList.length === 1 && fileList[0].uid === "-1");
      if (isDefaultImage) {
        const defaultImg = DEFAULT_CAMPAIGN_IMAGES[selectedType as number];
        if (defaultImg && defaultImg !== previewUrl) {
          setPreviewUrl(defaultImg);
          form.setFieldsValue({ Image: defaultImg });
          setFileList([
            {
              uid: "-1",
              name: defaultImg.split("/").pop() || "Default Image",
              status: "done",
              url: defaultImg,
              thumbUrl: defaultImg,
            },
          ]);
        }
      }
    }
  }, [selectedType, selectedCampaignChannel]);

  useEffect(() => {
    if (!selectedVoucher) return;
    if (isMessageUserEdited.current) {
      const currentMsg = form.getFieldValue("Message");
      if (!currentMsg) return;
      const resolved = resolveVoucherVars(currentMsg);
      if (resolved !== currentMsg) form.setFieldsValue({ Message: resolved });
      return;
    }
    if (!selectedType || !selectedCampaignChannel) return;
    if (selectedCampaignChannel === ChannelEnum.WhatsAppPremium) return;
    const defaultMsg = getDefaultMessage(selectedType, selectedCampaignChannel);
    if (defaultMsg)
      form.setFieldsValue({ Message: resolveVoucherVars(defaultMsg) });
  }, [selectedVoucher]);

  // ======================================================
  // 📤 Upload Handling
  // ======================================================

  const openNotificationWithIcon = () => {
    notif.error({
      message: "Error!",
      description:
        "An error occurred while uploading the image. Please try again with a different image or contact the administrator.",
      placement: "top",
    });
  };

  const uploadToS3 = async (file: File) => {
    let simInterval: number | undefined;
    let finished = false;
    const start = Date.now();

    const startSimulatedProgress = () => {
      setProgressUpload(1);
      simInterval = window.setInterval(() => {
        setProgressUpload((prev) => {
          if (finished) return prev;
          if (prev >= 75) return 75;
          const next = prev + 2;
          return next >= 75 ? 75 : next;
        });
        return;
      }, 100);
    };

    const clearSim = () => {
      if (simInterval) {
        window.clearInterval(simInterval);
        simInterval = undefined;
      }
    };

    try {
      setIsUploading(true);
      setProgressUpload(0);
      startSimulatedProgress();

      const res = await campaignService.uploadCampaignImage(file);
      finished = true;
      clearSim();
      if (!res || !res.result || !res.result.Url) {
        openNotificationWithIcon();
      }
      const elapsed = Date.now() - start;
      const remain = elapsed < 1000 ? 1000 - elapsed : 0;
      if (remain > 0) {
        await new Promise((r) => setTimeout(r, remain));
      }

      setProgressUpload(100);

      const imageUrl = res?.result?.Url as string | undefined;
      if (!imageUrl) throw new Error("Upload succeeded but URL is missing.");

      form?.setFieldsValue?.({ Image: imageUrl });
      setPreviewUrl(imageUrl);

      setFileList((lst) =>
        lst.length
          ? [
              {
                ...lst[0],
                status: "done",
                url: imageUrl,
                response: { Url: imageUrl },
              },
            ]
          : lst,
      );

      message.success("Image uploaded.");
    } catch (err: any) {
      finished = true;
      clearSim();
      message.error(
        err?.response?.data?.message || err?.message || "Upload failed",
      );
      setFileList([]);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      form?.setFieldsValue?.({ Image: undefined });
    } finally {
      setTimeout(() => setProgressUpload(0), 300);
      setIsUploading(false);
    }
  };

  const handleChangeUpload = (info: UploadChangeParam<UploadFile<any>>) => {
    const latestList = info.fileList.slice(-1);
    setFileList(latestList);
    if (latestList.length) setProgressUpload(0);
  };

  const beforeUpload = (file: File) => {
    const isValidType = ["image/jpeg", "image/png"].includes(file.type);
    const isLt5M = file.size / 1024 / 1024 <= 5;

    if (!isValidType) {
      message.error("Only JPG/PNG are allowed.");
      return Upload.LIST_IGNORE;
    }
    if (!isLt5M) {
      message.error("Max file size is 5 MB.");
      return Upload.LIST_IGNORE;
    }

    const url = URL.createObjectURL(file);

    const item: UploadFile = {
      uid: `${Date.now()}`,
      name: file.name,
      status: "uploading",
      originFileObj: file as any,
      url,
      thumbUrl: url,
    };
    setFileList([item]);

    uploadToS3(file);

    return false;
  };

  // ======================================================
  // 🔎 Search & Voucher Selection
  // ======================================================
  const handleRemove = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFileList([]);
    setProgressUpload(0);
  };

  // ======================================================
  // 🚫 Exclude Targeted Member Upload Handling
  // ======================================================
  const beforeUploadExclude = (file: File) => {
    const isExcel =
      file.name.toLowerCase().endsWith(".xlsx") ||
      file.name.toLowerCase().endsWith(".xls");
    if (!isExcel) {
      message.error("Only Excel files (.xlsx, .xls) are allowed.");
      return Upload.LIST_IGNORE;
    }
    const isLt5M = file.size / 1024 / 1024 <= 5;
    if (!isLt5M) {
      message.error("Max file size is 5 MB.");
      return Upload.LIST_IGNORE;
    }

    (async () => {
      try {
        setExcludeParsing(true);
        const { rows, members } = await parseExcludeMemberFile(file);
        if (!members.length) {
          message.error(
            "No members found in the uploaded file. Please use the example format.",
          );
          return;
        }
        setExcludeRows(rows);
        setExcludeMembers(members);
        setExcludeRawFile(file);
        setExcludeFileList([
          {
            uid: `${Date.now()}`,
            name: file.name,
            status: "done",
          },
        ]);
        onChangeRFM();
        message.success(`${members.length} members loaded to exclude.`);
      } catch (err) {
        console.error("Failed to parse exclude file:", err);
        message.error("Failed to read the Excel file. Please try again.");
      } finally {
        setExcludeParsing(false);
      }
    })();

    return false;
  };

  const handleRemoveExclude = () => {
    setExcludeFileList([]);
    setExcludeRows([]);
    setExcludeMembers([]);
    setExcludeRawFile(null);
    onChangeRFM();
  };

  const summary = selectedVoucher
    ? `${selectedVoucher.Name} | ${selectedVoucher.DiscountLabel}`
    : selectedEVoucher
      ? `${selectedEVoucher.Name} | ${selectedEVoucher.Price}`
      : "No rewards selected";

  const templatesSummary = selectedMessageTemplates
    ? `${selectedMessageTemplates.Name}`
    : "No templates selected";

  const blocked: BlockRange[] = parseBlockedRanges(
    listBlockDate,
    "YYYY-MM-DD HH:mm",
  );
  const isEmpty = (v: any) => v === undefined || v === null || v === "";
  const handleError = ({ errorFields }: any) => {
    if (errorFields && errorFields.length > 0) {
      form.scrollToField(errorFields[0].name, {
        behavior: "smooth",
        block: "center",
      });

      message.error(errorFields[0].errors?.[0]);
    }
  };

  const weekdayOptions = Object.entries(WeekDay)
    .filter(([key, value]) => typeof value === "number")
    .map(([key, value]) => ({
      label: key,
      value: value as number,
    }));

  useEffect(() => {
    if (selectedCampaignChannel === ChannelEnum.WhatsAppPremium) {
      form.setFieldsValue({
        Message: selectedMessageTemplates?.Content ?? "",
      });
      form.validateFields(["Message"]);
    }
  }, [selectedCampaignChannel, selectedMessageTemplates, form]);

  const onPaymentlater = async () => {
    setIsBillingModalOpen(false);
    setLoading(true);
    try {
      const resultId = await campaignService.createCampaignDetails(
        pendingPayload!,
        excludeRawFile ?? undefined,
      );
      if (!resultId) {
        alert("Failed to create campaign.");
        return;
      }
      navigate(
        `${RoutePaths.IFRAME_DETAIL_CAMPAIGN}${location.search}&id=${resultId}`,
      );
    } catch {
      alert("Failed to create campaign.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      onFinishFailed={handleError}
      initialValues={{
        PurchaseBrand: selectedPurchaseBrand,
        ScheduleType: TypeEnum.Onetime,
        Segments: [-2, -3, -4],
        CostPerMember: getChannelCost(selectedCampaignChannel),
        TemplateType: TemplatesTypeEnum.Default,
        InactiveDays: inactiveDays,
        Mode: ModeEnum.Refresh,
        ScheduleEndType: EndScheduleEnum.OnSpecificDate,
        Frequency: TypeEnum.Recurring_Weekly,
        Retargeting: false,
        CampaignDate: dayjs()
          .add(1, "day")
          .startOf("day")
          .hour(8)
          .minute(0)
          .second(0),
      }}>
      {contextHolder}
      <Card
        title={
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={5} style={{ margin: 0 }}>
                Create{" "}
                {selectedCampaignChannel
                  ? ChannelKeyMap[selectedCampaignChannel]
                  : ""}{" "}
                Campaign
              </Title>
            </Col>
            <Col>
              <Button
                color="primary"
                variant="outlined"
                onClick={() =>
                  navigateWithQuery(
                    RoutePaths.IFRAME_TARGET_MARKETING_HOME,
                    "campaign_channel",
                    null,
                  )
                }>
                Back
              </Button>
            </Col>
          </Row>
        }
        className="card-form-campaign">
        {/* {selectedCampaignChannel === ChannelEnum.WhatsAppStandard && (
          <Alert
            className=""
            type="info"
            description={
              <span>
                Campaign delivery is processed in batches, with a limit of 1,000
                recipients per day.
              </span>
            }
            showIcon
            style={{ marginBottom: 16 }}
          />
        )} */}
        {/* Campaign Details */}
        <Title level={5}>Campaign Details</Title>
        <Divider style={{ margin: "10px 0" }} />
        <Form.Item name="Status" hidden>
          <Input />
        </Form.Item>
        <FieldRow label="Campaign Name" required>
          <Form.Item
            name="Name"
            validateStatus={nameError ? "error" : ""}
            help={nameError}
            rules={[
              { required: true, message: "Please input campaign name" },
              {
                validator: async (_, value) => {
                  if (!value) {
                    setNameError("Please input campaign name");
                  }
                  if (value && value.trim() === "") {
                    setNameError(
                      "Campaign name cannot be empty or spaces only",
                    );
                  }

                  const invalidCharRegex = /[^a-zA-Z0-9 _-]/;
                  if (invalidCharRegex.test(value)) {
                    setNameError(
                      "Campaign name cannot contain special characters like & * @ # etc.",
                    );
                    return Promise.reject(
                      "Campaign name cannot contain special characters like & * @ # etc.",
                    );
                  }

                  if (nameError && nameError !== "") {
                    return Promise.reject(nameError);
                  }

                  return Promise.resolve();
                },
              },
            ]}
            style={{ marginBottom: 0 }}>
            <Input
              placeholder="Input Campaign Name"
              onChange={async (e) => {
                const value = e.target.value.trim();
                if (value) {
                  const isAvailable =
                    await FetchCheckAvailableCampaignName(value);
                  if (isAvailable) {
                    setNameError(null);
                    if (shortenLinkTimerRef.current)
                      clearTimeout(shortenLinkTimerRef.current);
                    shortenLinkTimerRef.current = setTimeout(() => {
                      const generatedLink = `${campaignInfo?.MemberPortalUrl}?utm_source=${
                        selectedCampaignChannel
                          ? ChannelKeyMap[selectedCampaignChannel]
                          : ""
                      }&utm_campaign=${value}&utm_medium=MemberPortal`;
                      fetchShortenLink(generatedLink);
                    }, 800);
                  } else {
                    setNameError(
                      "A campaign with this name already exists. Please enter a unique campaign name",
                    );
                  }
                }
              }}
            />
          </Form.Item>
        </FieldRow>
        <FieldRow label="Campaign Type" required>
          <Form.Item
            name="Type"
            rules={[{ required: true, message: "Select Campaign Type" }]}
            style={{ marginBottom: 0 }}>
            <Select
              placeholder="Select Campaign Type"
              options={CAMPAIGN_TYPE_OPTIONS}
              allowClear
              onChange={(value) => {
                setSelectedEVoucher(null);
                setSelectedVoucher(null);
                setSelectedMessageTemplates(null);
                if (value !== CampaignTypeEnum.EVoucher && evoucherCustom) {
                  setEvoucherCustom(false);
                  const name = form.getFieldValue("Name")?.trim();
                  if (
                    name &&
                    campaignInfo?.MemberPortalUrl &&
                    selectedCampaignChannel
                  ) {
                    fetchShortenLink(
                      `${campaignInfo.MemberPortalUrl}?utm_source=${ChannelKeyMap[selectedCampaignChannel]}&utm_campaign=${name}&utm_medium=MemberPortal`,
                    );
                  }
                }
              }}
            />
          </Form.Item>
          <div style={{ color: "#8c8c8c", marginTop: 6 }}>
            Choose the reward type that defines how revenue will be tracked.
          </div>
        </FieldRow>
        <FieldRow label="Campaign Template Type" required>
          <Form.Item
            name="TemplateType"
            rules={[
              { required: true, message: "Select Campaign Template Type" },
            ]}
            style={{ marginBottom: 0 }}>
            <Select
              placeholder="Select Campaign Template Type"
              options={CAMPAIGN_TEMPLATES_OPTIONS}
              value={selectedTemplate}
              onChange={(value) => {
                setSelectedTemplate(value);
                onChangeRFM();
              }}
            />
            <div style={{ color: "#8c8c8c", marginTop: 6 }}>
              {selectedTemplate === TemplatesTypeEnum.Default
                ? `This template uses a pre-configured RFM segmentation targeting members with no transactions or visits in the last ${inactiveDays} days`
                : "Fully customizable RFM segmentation. Select your own period, segments and criteria."}
            </div>
          </Form.Item>
        </FieldRow>
        {selectedTemplate === TemplatesTypeEnum.Default && (
          <FieldRow label="Inactive Period (Days)">
            <Form.Item
              name="InactiveDays"
              rules={[{ required: true, message: "Please input days" }]}
              style={{ marginBottom: 0 }}>
              <InputNumber
                min={1}
                value={inactiveDays}
                onChange={(value) => {
                  setInactiveDays(value || 60);
                  onChangeRFM();
                }}
                placeholder="Inactive Days"
                addonAfter="Days"
                style={{ width: "100%" }}
              />
            </Form.Item>
            <div style={{ color: "#8c8c8c", marginTop: 6 }}>
              Define the number of days since a members last transaction or
              visit to be considered inactive.
            </div>
          </FieldRow>
        )}
        <FieldRow label="Selected RFM Period" required>
          <Form.Item
            name="Period"
            rules={[{ required: true, message: "Please select RFM Period" }]}
            style={{ marginBottom: 0 }}>
            <Select
              onChange={onChangeRFM}
              placeholder="Select RFM Period"
              options={RfmPeriod}
              style={{ width: "100%" }}
              disabled={selectedTemplate === TemplatesTypeEnum.Default}
            />
          </Form.Item>
        </FieldRow>

        <FieldRow label="Target RFM Segment" required>
          <Form.Item
            name="Segments"
            rules={[{ required: true, message: "Select Target RFM Segment" }]}
            style={{ marginBottom: 0 }}>
            <Select
              onChange={onChangeRFM}
              mode="multiple"
              placeholder="Select Target RFM Segment"
              options={SegmentOptions}
              allowClear
              style={{ width: "100%" }}
              maxTagCount={0}
              maxTagPlaceholder={(omittedValues) => {
                const total = SegmentOptions?.length ?? 0;
                const selected = omittedValues?.length ?? 0;

                if (selected === 0) return null;
                if (selected === total) return "All Segments";

                return `${selected}/${total} selected`;
              }}
              disabled={selectedTemplate === TemplatesTypeEnum.Default}
            />
          </Form.Item>
        </FieldRow>
        <FieldRow label="RFM Criteria Profile" required>
          <Form.Item
            style={{ marginBottom: 0 }}
            name="Profile"
            rules={[
              { required: true, message: "Please select RFM Criteria Profile" },
            ]}>
            <Select
              onChange={onChangeRFM}
              placeholder="Select RFM Criteria Profile"
              options={ProfileOptions}
              style={{ width: "100%" }}
              disabled={selectedTemplate === TemplatesTypeEnum.Default}
            />
          </Form.Item>
        </FieldRow>
        <FieldRow label="Target Demographics (age)">
          <Row gutter={[12, 12]}>
            {/* FROM AGE */}
            <Col xl={4} xs={10}>
              <Form.Item
                name={["ageRange", "from"]}
                style={{ marginBottom: 0 }}
                dependencies={[["ageRange", "to"]]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const to = getFieldValue(["ageRange", "to"]);
                      if (isEmpty(value) && isEmpty(to))
                        return Promise.resolve();
                      if (isEmpty(value) && !isEmpty(to)) {
                        return Promise.reject(
                          new Error("Please input minimum age"),
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}>
                <InputNumber
                  min={0}
                  step={1}
                  placeholder="From Age"
                  style={{ width: "100%" }}
                  onChange={onChangeRFM}
                />
              </Form.Item>
            </Col>

            <Col span={1} style={{ textAlign: "center", lineHeight: "32px" }}>
              <Divider
                style={{
                  margin: "15px 0",
                  borderBlockStart: "1px solid black",
                }}
              />
            </Col>

            {/* TO AGE */}
            <Col xl={4} xs={10}>
              <Form.Item
                name={["ageRange", "to"]}
                style={{ marginBottom: 0 }}
                dependencies={[["ageRange", "from"]]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const from = getFieldValue(["ageRange", "from"]);
                      if (isEmpty(value) && isEmpty(from))
                        return Promise.resolve();
                      if (!isEmpty(from) && isEmpty(value)) {
                        return Promise.reject(
                          new Error("Please input maximum age"),
                        );
                      }
                      if (!isEmpty(from) && !isEmpty(value)) {
                        const a = Number(from);
                        const b = Number(value);
                        if (!(b > a)) {
                          return Promise.reject(
                            new Error("To age must be higher than From age"),
                          );
                        }
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}>
                <InputNumber
                  min={0}
                  step={1}
                  placeholder="To Age"
                  style={{ width: "100%" }}
                  onChange={onChangeRFM}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <div style={{ color: "#8c8c8c", marginTop: 6 }}>
              Specify the age range of members to include in the campaign.
            </div>
          </Row>
        </FieldRow>
        <FieldRow label="Target Geographic (Postal Code)">
          <Form.Item style={{ marginBottom: 0 }} name="PostalCode">
            <Select
              mode="multiple"
              placeholder="Select Postal Code"
              options={PostalCodeOptions}
              value={postalFilter}
              onChange={(value: any) => {
                onChangeRFM();
                setPostalFilter(value);
              }}
              style={{ width: "100%" }}
              showSearch={false}
              filterOption={false}
              maxTagCount={0}
              maxTagPlaceholder={() => {
                if (!postalFilter || postalFilter.length === 0) return null;
                if (
                  postalFilter.includes("all") ||
                  postalFilter.length === postalTotal
                ) {
                  return "All Postal Sectors";
                }
                return `${postalFilter.length}/${postalTotal} selected`;
              }}
            />
          </Form.Item>
          <div style={{ color: "#8c8c8c", marginTop: 6 }}>
            2 digits of 6-digit postal codes
          </div>
        </FieldRow>

        <FieldRow label="Purchase Brand" required>
          <Form.Item
            style={{ marginBottom: 0 }}
            name="PurchaseBrand"
            rules={[{ required: true, message: "Please select Brand" }]}>
            <Select
              mode="multiple"
              placeholder="Select Brands"
              maxTagCount={0}
              onChange={handleChangePurchaseBrand}
              value={
                form.getFieldValue("PurchaseBrand")?.includes(AllBrand)
                  ? [AllBrand, ...listPurchaseBrandFilter]
                  : form.getFieldValue("PurchaseBrand")
              }
              maxTagPlaceholder={() => {
                const value = form.getFieldValue("PurchaseBrand") || [];
                return value.includes(AllBrand)
                  ? AllBrand
                  : `${value.length}/${listPurchaseBrandFilter.length} selected`;
              }}>
              <Option key={AllBrand} value={AllBrand}>
                {AllBrand}
              </Option>
              {listPurchaseBrandFilter.map((brand) => (
                <Option key={brand} value={brand}>
                  {brand}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </FieldRow>
        <FieldRow label="Exclude Targeted Members (Optional)">
          <Form.Item style={{ marginBottom: 0 }}>
            <ExcludeMemberUpload
              fileList={excludeFileList}
              memberCount={excludeMembers.length}
              parsing={excludeParsing}
              beforeUpload={beforeUploadExclude}
              onRemove={handleRemoveExclude}
              onPreview={() => setIsExcludePreviewOpen(true)}
            />
          </Form.Item>
        </FieldRow>
        <FieldRow
          label={
            <>
              Estimated Total Members{" "}
              {/* <DownloadOutlined onClick={handleDownloadEstimated} />{" "} */}
            </>
          }
          // label="Estimated Total Members"
        >
          <Form.Item
            validateStatus={totalMembersError ? "error" : ""}
            help={totalMembersError || undefined}
            style={{ marginBottom: 0 }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <InputNumber
                style={{ width: isMobileOrTablet ? "70%" : "87%" }}
                value={estimatedTotalMember}
                addonAfter="Members"
                disabled
              />
              <Button
                disabled={!(hasCriteria && hasPeriod && hasSegment)}
                color="primary"
                variant="outlined"
                loading={loadingRefreshMember}
                onClick={handleRefreshMember}
                style={{
                  width: isMobileOrTablet ? "27%" : "12%",
                  marginLeft: "auto",
                }}>
                {loadingRefreshMember ? (
                  "Loading ..."
                ) : (
                  <>
                    <SyncOutlined /> Reload
                  </>
                )}
              </Button>
            </div>

            <div style={{ color: "#8c8c8c", marginTop: 6 }}>
              {/* 1️⃣ RFM changed → highest priority */}
              {changeRFM && (
                <Alert
                  type="error"
                  showIcon
                  description="The displayed total member count may not be accurate. Please click Reload to ensure the latest data is reflected based on your current selections."
                />
              )}

              {/* 2️⃣ Ready but not reloaded */}
              {!changeRFM &&
                hasCriteria &&
                hasPeriod &&
                hasSegment &&
                !alreadyReload && (
                  <Alert
                    type="info"
                    showIcon
                    description="Estimation total number of members matched based on your selected RFM Period, Target Segment(s), RFM Criteria Profile, Target Demographics, and Target Geographic. Click Reload to view the result."
                  />
                )}

              {/* 3️⃣ Zero result */}
              {!changeRFM && !alreadyReload && estimatedTotalMember === 0 && (
                <div style={{ fontSize: 13, color: "#595959" }}>
                  Estimation total number of members matched based on your
                  selected RFM Period, Target Segment(s), RFM Criteria Profile,
                  Target Demographics, and Target Geographic.
                </div>
              )}

              {/* 4️⃣ Has members → Preview */}
              {!changeRFM && alreadyReload && estimatedTotalMember > 0 && (
                <Alert
                  type="info"
                  showIcon
                  icon={
                    <img
                      src="/icon/icon-memberlist.svg"
                      alt="members"
                      style={{ width: 40, height: 40 }}
                    />
                  }
                  message={
                    <Row align="middle" justify="space-between">
                      <Col>
                        <div style={{ fontSize: 20, fontWeight: 600 }}>
                          {estimatedTotalMember}
                        </div>
                        <div style={{ fontSize: 13, color: "#595959" }}>
                          Estimation total members will receive this campaign
                        </div>
                      </Col>

                      <Col>
                        <Button
                          type="primary"
                          icon={<EyeOutlined />}
                          onClick={openPreview}>
                          Preview Members
                        </Button>
                      </Col>
                    </Row>
                  }
                />
              )}
            </div>
          </Form.Item>
        </FieldRow>

        <FieldRow label="Channel Cost per Message">
          <Space.Compact style={{ width: "100%" }}>
            <Input
              disabled
              value="$"
              style={{ width: 40, textAlign: "center" }}
            />
            <Form.Item name="CostPerMember" noStyle>
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={0.01}
                stringMode
                placeholder="0.00"
                disabled
              />
            </Form.Item>
          </Space.Compact>
          <div style={{ color: "#8c8c8c", marginTop: 6 }}>
            Cost to send one message, based on selected channel.
          </div>
        </FieldRow>

        <FieldRow label="Estimated Budget">
          <InputNumber
            style={{ width: "100%" }}
            value={
              estimatedTotalCost ? Number(estimatedTotalCost.toFixed(2)) : 0
            }
            disabled
          />
          <div style={{ color: "#8c8c8c", marginTop: 6 }}>
            Automatically calculated cost: Total Members X Channel Cost{" "}
            {selectedCampaignChannel === ChannelEnum.Sms && "X Total SMS"}
          </div>
        </FieldRow>

        <FieldRow label="Schedule Campaign" required>
          <Form.Item name="ScheduleType" noStyle rules={[{ required: true }]}>
            <Radio.Group onChange={onScheduleChange}>
              <Space direction="vertical">
                <Radio value={TypeEnum.Onetime}>
                  <Text style={{ fontWeight: "normal" }}>
                    {" "}
                    One‑Time Campaign
                  </Text>
                  <div style={{ color: "grey", fontWeight: "normal" }}>
                    Send this campaign once at a specific date and time you
                    choose.
                  </div>
                </Radio>
                <Radio value={TypeEnum.Recurring_Weekly}>
                  <Text style={{ fontWeight: "normal" }}>
                    {" "}
                    Recurring Campaign
                  </Text>
                  <div style={{ color: "grey", fontWeight: "normal" }}>
                    Send this campaign on a repeated schedule—weekly or monthly.
                  </div>
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </FieldRow>
        {scheduleType === TypeEnum.Onetime && (
          <FieldRow label="Target Members Mode" required>
            <Form.Item name="Mode" noStyle rules={[{ required: true }]}>
              <Radio.Group onChange={onModeChange} style={{ width: "100%" }}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Radio value={ModeEnum.Refresh}>
                    <Text style={{ fontWeight: "normal" }}>
                      Refresh target before sending
                    </Text>
                    <div style={{ color: "grey", fontWeight: "normal" }}>
                      Target members will be refreshed 1 day before send time.
                    </div>
                  </Radio>
                  <Alert
                    description={
                      <>
                        Target members may change before sending. This can
                        affect final reach and budget usage.
                      </>
                    }
                    type="warning"
                    showIcon
                  />
                  <Radio value={ModeEnum.Fixed}>
                    <Text style={{ fontWeight: "normal" }}>
                      Use fixed target
                    </Text>
                    <div
                      style={{
                        color: "grey",
                        fontWeight: "normal",
                      }}>
                      Target members remains the same as when the campaign was
                      created.{" "}
                    </div>
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </FieldRow>
        )}

        <FieldRow label="Send Date & Time" required>
          <Form.Item
            name="CampaignDate"
            style={{ marginBottom: 0 }}
            rules={[{ required: true, message: "Select date and time" }]}>
            <DatePicker
              showTime={{
                format: "HH:mm",
                minuteStep: 15,
                showSecond: false,
                defaultValue: dayjs().hour(8).minute(0).second(0),
              }}
              disabledDate={(current) => {
                if (!current) return false;
                if (!current.isAfter(startOfToday, "day")) return true;
                return current.isBefore(startOfToday, "day");
              }}
              disabledTime={(date) => {
                if (!date) return {};
                const base = buildDisabledTime(date, blocked);
                const withPast = applyPastTimeToday(date, base);
                // Block 00:00–08:00 on every day (hours 0..7).
                const before8 = Array.from({ length: 8 }, (_, i) => i);
                const baseHours = withPast.disabledHours();
                return {
                  ...withPast,
                  disabledHours: () =>
                    Array.from(new Set([...baseHours, ...before8])).sort(
                      (a, b) => a - b,
                    ),
                };
              }}
              format="YYYY-MM-DD HH:mm"
              style={{ width: "100%", height: "unset !important" }}
              placeholder="Select Send Date & Time"
              onChange={(date) => setCampaignDate(date)}
            />
            <div style={{ color: "#8c8c8c", marginTop: 6 }}>
              Campaigns must be scheduled at least 1 day in advance. Same-day
              scheduling is not available.
            </div>
          </Form.Item>

          {campaignDate && (
            <Alert
              description={
                <>
                  {
                    <>
                      Your campaign will start at {formattedStart} and is
                      estimated to complete by {formattedEnd}.
                    </>
                  }
                </>
              }
              type="info"
              showIcon
              style={{ marginTop: 10 }}
            />
          )}
        </FieldRow>

        {scheduleType !== TypeEnum.Onetime && (
          //  (
          //   <FieldRow label="Send Date & Time" required>
          //     <Form.Item
          //       name="CampaignDate"
          //       style={{ marginBottom: 0 }}
          //       rules={[{ required: true, message: "Select date and time" }]}
          //     >
          //       <DatePicker
          //         showTime={{
          //           format: "HH:mm",
          //           minuteStep: 15,
          //           showSecond: false,
          //         }}
          //         disabledDate={(current) => {
          //           if (!current) return false;

          //           return (
          //             current.isBefore(startOfToday, "day") ||
          //             current.isAfter(endOfNextMonth, "day")
          //           );
          //         }}
          //         disabledTime={(date) => {
          //           if (!date) return {};
          //           const base = buildDisabledTime(date, blocked);
          //           const test = applyPastTimeToday(date, base);
          //           return test;
          //         }}
          //         format="YYYY-MM-DD HH:mm"
          //         style={{ width: "100%", height: "unset !important" }}
          //         placeholder="Select Send Date & Time"
          //       />
          //     </Form.Item>

          //     {campaignDate && (
          //       <Alert
          //         description={
          //           <>
          //             {
          //               <>
          //                 Your campaign will start at {formattedStart} and is
          //                 estimated to complete by {formattedEnd}.
          //               </>
          //             }
          //           </>
          //         }
          //         type="info"
          //         showIcon
          //         style={{ marginTop: 10 }}
          //       />
          //     )}
          //   </FieldRow>
          // ) :
          <>
            <FieldRow label="Frequency" required>
              <Form.Item
                name="Frequency"
                rules={[{ required: true, message: "Select frequency" }]}
                noStyle>
                <Select
                  placeholder="Frequency"
                  style={{ width: "100%" }}
                  className="w-100"
                  defaultValue={TypeEnum.Recurring_Weekly}
                  options={[
                    { value: TypeEnum.Recurring_Weekly, label: "Weekly" },
                    { value: TypeEnum.Recurring_Monthly, label: "Monthly" },
                  ]}
                />
              </Form.Item>
            </FieldRow>
          </>
        )}

        {frequencyValue === TypeEnum.Recurring_Weekly && (
          <FieldRow label="Repeat on Day(s)" required>
            <Form.Item
              name="RepeatDays"
              rules={[
                {
                  required: true,
                  message: "Please select at least one day.",
                },
              ]}
              style={{ marginBottom: 0 }}>
              <Select
                mode="multiple"
                options={weekdayOptions}
                placeholder="Select day(s) of the week"
                className="w-100"
                optionFilterProp="label"
                maxTagCount={0}
                maxTagPlaceholder={(selectedItems) =>
                  `${selectedItems.length} Selected Day(s)`
                }
              />
            </Form.Item>
          </FieldRow>
        )}

        {frequencyValue === TypeEnum.Recurring_Monthly && (
          <FieldRow label="Repeat Every" required>
            {" "}
            <Form.Item
              name="RepeatMonth"
              rules={[
                {
                  required: true,
                  message: "Please enter the repeat interval in months.",
                },
                {
                  type: "number",
                  min: 1,
                  message: "Repeat interval must be at least 1 month.",
                },
              ]}
              style={{ marginBottom: 0 }}>
              <InputNumber
                className="w-100"
                value={estimatedTotalMember}
                addonAfter="Month(s)"
                placeholder={"Input the repeat interval in months."}
                precision={0}
                step={1}
              />
            </Form.Item>
          </FieldRow>
        )}
        {scheduleType !== TypeEnum.Onetime && (
          <FieldRow label="End Schedule" required>
            <Form.Item
              name="ScheduleEndType"
              noStyle
              rules={[{ required: true }]}>
              <Radio.Group onChange={onEndScheduleChange}>
                <Space direction="vertical">
                  {/* <Radio value={EndScheduleEnum.Never}>
                    <Text style={{ fontWeight: "normal" }}>Never</Text>
                    <div style={{ color: "grey", fontWeight: "normal" }}>
                      This campaign will repeat indefinitely
                    </div>
                  </Radio> */}
                  <Radio value={EndScheduleEnum.OnSpecificDate}>
                    <Text style={{ fontWeight: "normal" }}>
                      {" "}
                      On specific date{" "}
                    </Text>
                    <div style={{ color: "grey", fontWeight: "normal" }}>
                      Ends on the selected date.{" "}
                    </div>
                  </Radio>
                  <Radio value={EndScheduleEnum.AfterOccurrences}>
                    <Text style={{ fontWeight: "normal" }}>
                      After X occurrences
                    </Text>
                    <div style={{ color: "grey", fontWeight: "normal" }}>
                      {" "}
                      Ends after the specified number of times.
                    </div>
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </FieldRow>
        )}

        {scheduleType !== TypeEnum.Onetime &&
          endScheduleType === EndScheduleEnum.OnSpecificDate && (
            <FieldRow label="End Date & Time" required>
              <Form.Item
                name="EndDate"
                rules={[
                  {
                    required: true,
                    message: "Please select end date and time",
                  },
                ]}
                style={{ marginBottom: 0 }}>
                <DatePicker
                  showTime={{
                    format: "HH:mm",
                    minuteStep: 15,
                    showSecond: false,
                  }}
                  format="YYYY-MM-DD HH:mm"
                  style={{ width: "100%", height: "unset !important" }}
                  placeholder="Select End Date & Time"
                  disabledDate={(current) => {
                    if (!current) return false;
                    const campaignDate = form.getFieldValue("CampaignDate");

                    if (current.isBefore(startOfToday, "day")) {
                      return true;
                    }

                    if (campaignDate && !current.isAfter(campaignDate)) {
                      return true;
                    }

                    return false;
                  }}
                />
              </Form.Item>
            </FieldRow>
          )}

        {scheduleType !== TypeEnum.Onetime &&
          endScheduleType === EndScheduleEnum.AfterOccurrences && (
            <FieldRow label="Number of Occurrences" required>
              <Form.Item
                name="MaxOccurrences"
                rules={[
                  {
                    required: true,
                    message: "Please input number of occurrences",
                  },
                  {
                    type: "number",
                    min: 1,
                    message: "Must be at least 1",
                  },
                ]}
                style={{ marginBottom: 0 }}>
                <InputNumber
                  min={1}
                  style={{ width: "100%" }}
                  placeholder="Input number of occurrences"
                  addonAfter="Times"
                  precision={0}
                  step={1}
                />
              </Form.Item>
            </FieldRow>
          )}
        {scheduleType !== TypeEnum.Onetime && (
          <FieldRow label="Audience Re-targeting" required>
            <Form.Item
              name="Retargeting"
              noStyle
              rules={[
                { required: true, message: "Please select retargeting logic" },
              ]}>
              <Radio.Group style={{ width: "100%" }}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Radio value={false}>
                    <Text style={{ fontWeight: "normal" }}>
                      Prevent repeated targeting
                    </Text>
                    <div style={{ color: "grey", fontWeight: "normal" }}>
                      {selectedType === CampaignTypeEnum.Voucher ||
                      selectedType === CampaignTypeEnum.VoucherPoint
                        ? "Member will only be targeted again after previous voucher expiry"
                        : "Each member can only receive this campaign once"}
                    </div>
                  </Radio>
                  <Radio value={true}>
                    <Text style={{ fontWeight: "normal" }}>
                      Allow repeated targeting
                    </Text>
                    <div style={{ color: "grey", fontWeight: "normal" }}>
                      Member can receive this recurring campaign multiple times.
                    </div>
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </FieldRow>
        )}
        <FieldRow
          label={
            scheduleType === TypeEnum.Onetime
              ? "Budget Amount Entire Campaign"
              : "Budget per Recurring Run"
          }
          required>
          <Space.Compact style={{ width: "100%" }}>
            <Form.Item
              name="Budget"
              validateTrigger={["onChange", "onBlur"]}
              rules={[
                {
                  required: true,
                  message: "Please input total campaign budget",
                },
                {
                  validator: async (_, value) => {
                    if (value === undefined || value === null || value === "") {
                      return Promise.resolve();
                    }
                    return Number(value) >= cost
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(`Minimum required budget: ${cost}`),
                        );
                  },
                },
              ]}
              style={{ width: "100%", marginBottom: 0 }}>
              <InputNumber
                min={0}
                step={0.01}
                addonBefore="$"
                style={{ width: "100%" }}
                placeholder="Input Total Campaign Budget"
              />
            </Form.Item>
          </Space.Compact>
          <div style={{ color: "#8c8c8c", marginTop: 6 }}>
            Applies to this{" "}
            {scheduleType === TypeEnum.Onetime ? "one‑time" : "recurring"}{" "}
            campaign.
          </div>
        </FieldRow>

        <FieldRow label="Maximum Reach">
          <InputNumber
            style={{ width: "100%" }}
            value={maxReach}
            addonAfter="Members"
            formatter={(value) => {
              if (!value) return `0 / ${estimatedTotalMember}`;
              return `${Number(value)} / ${estimatedTotalMember}`;
            }}
            disabled
          />
          {maxReach > estimatedTotalMember ? (
            <Alert
              className="alert-info-budget-higher"
              type="info"
              showIcon
              style={{ padding: "8px 12px", marginTop: 10 }}
              description={
                <div style={{ fontSize: 14, lineHeight: 1.2 }}>
                  <div style={{ fontWeight: "bold", marginBottom: 4 }}>
                    Your budget is higher than required. Some budget may not be
                    used.
                  </div>
                  <div>
                    Adjust your budget or expand your targeting to utilize it
                    fully.
                  </div>
                </div>
              }
            />
          ) : maxReach === estimatedTotalMember ? (
            <div style={{ color: "#8c8c8c", marginTop: 10 }}>
              Recipients are selected from the top of the preview member list,
              up to the maximum number covered by your current budget.
            </div>
          ) : (
            <Alert
              style={{ marginTop: 10 }}
              description={
                <>
                  Only part of your target members will be reached based on your
                  budget.
                </>
              }
              type="warning"
              showIcon
            />
          )}
        </FieldRow>

        {/* <Row>
          <Col span={24}>
            <Alert
              message={<strong> Important Notice</strong>}
              description={
                <>
                  Estimation Total Members and Estimated Budget displayed are
                  estimates based on currently selected criteria. Actual member
                  count and budget used when the campaign runs may differ due to
                  real-time data changes, system conditions, or other factors.
                </>
              }
              type="warning"
              showIcon
            />
          </Col>
        </Row> */}

        {/* Message Content */}
        <Title level={5} style={{ marginBottom: 12 }}>
          Message Content
        </Title>

        <Divider />
        {SHOW_REWARD_TYPES.includes(selectedType) && (
          <FieldRow label="Rewards to Include" required>
            <Form.Item
              name="Voucher"
              rules={[
                {
                  validator: (_, value) => {
                    if (selectedVoucher || selectedEVoucher) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Please choose rewards to include"),
                    );
                  },
                },
              ]}
              style={{ width: "100%", marginBottom: 0 }}>
              {selectedVoucher || selectedEVoucher ? (
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    readOnly
                    value={summary}
                    addonBefore={
                      <span style={{ color: "#8c8c8c" }}>
                        Rewards Selected:
                      </span>
                    }
                    prefix={
                      selectedVoucher || selectedEVoucher ? (
                        <CheckCircleFilled style={{ color: "#52c41a" }} />
                      ) : (
                        <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
                      )
                    }
                    suffix={
                      selectedVoucher || selectedEVoucher ? (
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteFilled />}
                          onClick={() => {
                            setSelectedKey(null);
                            setSelectedVoucher(null);
                            setSelectedEVoucher(null);
                          }}
                        />
                      ) : null
                    }
                    style={{
                      background: "#f5f5f5",
                      fontWeight: selectedVoucher ? 600 : 400,
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    }}
                  />
                  <Button
                    type="primary"
                    onClick={() => setVoucherOpen(true)}
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}>
                    Change
                  </Button>
                </Space.Compact>
              ) : (
                <Row gutter={[12, 12]}>
                  <Col lg={4} xs={24}>
                    <Button
                      className="w-100"
                      onClick={() => setVoucherOpen(true)}
                      type="primary">
                      Select Rewards
                    </Button>
                  </Col>
                  <Col
                    lg={20}
                    xs={24}
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}>
                    <Text type="secondary">No rewards selected</Text>
                  </Col>
                </Row>
              )}
            </Form.Item>
          </FieldRow>
        )}
        {(selectedCampaignChannel === ChannelEnum.WhatsAppStandard ||
          selectedCampaignChannel === ChannelEnum.WhatsAppPremium ||
          selectedCampaignChannel === ChannelEnum.Email) && (
          <FieldRow label="Campaign Image (Optional)">
            <Form.Item name="Image" style={{ width: "100%", marginBottom: 0 }}>
              <Row gutter={[12, 12]}>
                <Col lg={4} xs={24}>
                  <Upload
                    accept="image/png,image/jpeg"
                    listType="picture"
                    showUploadList={false}
                    beforeUpload={beforeUpload}
                    fileList={fileList}
                    maxCount={1}
                    className="w-100"
                    onChange={handleChangeUpload}>
                    <Button
                      className="w-100"
                      disabled={previewUrl ? true : false}>
                      Choose File
                    </Button>
                  </Upload>
                </Col>
                <Col lg={20} xs={24}>
                  {" "}
                  {fileList.length === 0 ? (
                    <Text type="secondary">
                      No file chosen <br />
                      Recommended Size: 1080×1080 (1:1) or 1920×1080 (16:9),
                      JPG/PNG, Max 5 MB.
                    </Text>
                  ) : isUploading ? (
                    <Progress
                      percent={progressUpload}
                      size="small"
                      status="active"
                    />
                  ) : (
                    <Space>
                      <Text>{fileList[0].name} </Text>
                      <Button
                        icon={<DeleteOutlined />}
                        type="text"
                        danger
                        onClick={handleRemove}>
                        Remove
                      </Button>
                    </Space>
                  )}
                </Col>
              </Row>
              <Row style={{ marginTop: 10 }}>
                {previewUrl && (
                  <AntdImage
                    key={previewUrl}
                    src={previewUrl}
                    alt="Campaign preview"
                    width={250}
                    height={150}
                    style={{
                      objectFit: "contain",
                      borderRadius: 8,
                      border: "1px solid #f0f0f0",
                    }}
                    preview={{ src: previewUrl }}
                  />
                )}
              </Row>
            </Form.Item>
          </FieldRow>
        )}

        {/* <FieldRow label="Message Template">
          <Form.Item name="messageTemplate" noStyle>
            <Select
              placeholder="Select Message Template"
              options={MessageTemplates}
              allowClear
              style={{ width: "100%" }}
            />
          </Form.Item>
        </FieldRow> */}

        {selectedCampaignChannel === ChannelEnum.Email && (
          <>
            <FieldRow label="Email Subject">
              <Form.Item
                name="MessageSubject"
                rules={[
                  {
                    max: 50,
                    message: "Email Subject cannot exceed 50 characters",
                  },
                ]}>
                <Input
                  maxLength={50}
                  showCount
                  placeholder="Write subject line of the email"
                  onChange={() => {
                    isMessageUserEdited.current = true;
                  }}
                />
              </Form.Item>
            </FieldRow>

            <FieldRow label="Preheader">
              <Form.Item
                name="MessagePreheader"
                rules={[
                  {
                    max: 70,
                    message: "Preheader cannot exceed 70 characters",
                  },
                ]}>
                <Input
                  maxLength={70}
                  showCount
                  placeholder="Write short summary shown in inbox preview"
                  onChange={() => {
                    isMessageUserEdited.current = true;
                  }}
                />
              </Form.Item>
            </FieldRow>
          </>
        )}

        <FieldRow label="Message Content" required>
          {selectedCampaignChannel !== ChannelEnum.WhatsAppPremium &&
            selectedType > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  marginBottom: 8,
                }}>
                {selectedCampaignChannel === ChannelEnum.Email && (
                  <Button
                    size="small"
                    onClick={handleOpenEmailPreview}
                    disabled={!selectedType || !brandId}>
                    Preview Email Template
                  </Button>
                )}

                <Button size="small" onClick={applyDefaultMessage}>
                  Use Default Message
                </Button>
              </div>
            )}
          <Form.Item
            name="Message"
            style={{
              marginBottom: 0,
            }}
            rules={[
              {
                required: true,
                message: "Enter Message Content",
                validator: async (_, value) => {
                  // if (isFirstChange.current) {
                  //   isFirstChange.current = false;
                  //   return;
                  // }
                  const text = value?.replace(/<[^>]*>/g, "").trim();
                  return text
                    ? Promise.resolve()
                    : Promise.reject("Enter Message Content");
                },
              },
            ]}>
            {selectedCampaignChannel === ChannelEnum.WhatsAppStandard && (
              <EditorQuill
                value={msg}
                onChange={(val) => {
                  if (isFirstChange.current) return;
                  isMessageUserEdited.current = true;
                  form.setFieldsValue({ Message: val });
                }}
              />
            )}
            {selectedCampaignChannel === ChannelEnum.WhatsAppPremium &&
              (selectedMessageTemplates ? (
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    readOnly
                    value={templatesSummary}
                    addonBefore={
                      <span style={{ color: "#8c8c8c" }}>
                        Template Selected:
                      </span>
                    }
                    prefix={
                      selectedMessageTemplates ? (
                        <CheckCircleFilled style={{ color: "#52c41a" }} />
                      ) : (
                        <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
                      )
                    }
                    suffix={
                      selectedMessageTemplates ? (
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteFilled />}
                          onClick={() => {
                            setSelectedMessageTemplates(null);
                          }}
                        />
                      ) : null
                    }
                    style={{
                      background: "#f5f5f5",
                      fontWeight: selectedMessageTemplates ? 600 : 400,
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    }}
                  />
                  <Button
                    type="primary"
                    onClick={() => setMessageTemplatesOpen(true)}
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}>
                    Change
                  </Button>
                </Space.Compact>
              ) : (
                <Row gutter={[12, 12]}>
                  <Col lg={4} xs={24}>
                    <Button
                      className="w-100"
                      onClick={() => setMessageTemplatesOpen(true)}
                      type="primary"
                      disabled={!selectedType}>
                      Select Template
                    </Button>
                  </Col>
                  <Col
                    lg={20}
                    xs={24}
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}>
                    <Text type="secondary">No template selected</Text>
                  </Col>
                </Row>
              ))}

            {selectedCampaignChannel === ChannelEnum.Sms && (
              <Input.TextArea
                rows={4}
                placeholder="Enter Message Content"
                autoSize
                onChange={() => {
                  isMessageUserEdited.current = true;
                }}
              />
            )}

            {selectedCampaignChannel === ChannelEnum.Email &&
              (parsedEmailTemplate ? (
                <EmailTemplateEditor
                  template={parsedEmailTemplate}
                  imageUrl={previewUrl ?? undefined}
                  renderEditable={(fieldName, defaultContent) => (
                    <EditorQuill
                      ref={(el) => {
                        if (el) emailQuillRefs.current.set(fieldName, el);
                        else emailQuillRefs.current.delete(fieldName);
                      }}
                      value={emailFieldValues[fieldName] ?? defaultContent}
                      enableButtonInsert
                      onFocus={() => {
                        activeEmailFieldRef.current = fieldName;
                      }}
                      onChange={(val) => {
                        isMessageUserEdited.current = true;
                        setEmailFieldValues((prev) => ({
                          ...prev,
                          [fieldName]: val,
                        }));
                        form.setFieldsValue({ Message: val });
                      }}
                    />
                  )}
                />
              ) : (
                <EditorQuill
                  value={msg}
                  onChange={(val) => {
                    isMessageUserEdited.current = true;
                    form.setFieldsValue({ Message: val });
                  }}
                />
              ))}
          </Form.Item>
        </FieldRow>
        {selectedCampaignChannel !== ChannelEnum.WhatsAppPremium && (
          <FieldRow
            label={
              <span>
                Message Variables{" "}
                <Tooltip
                  title={
                    <>
                      Personalize your message with variables.
                      <br />
                      These will be replaced with real data when the campaign is
                      sent
                    </>
                  }
                  placement="right">
                  <InfoCircleOutlined
                    style={{ color: "#8c8c8c", cursor: "pointer" }}
                  />
                </Tooltip>
              </span>
            }>
            {" "}
            <div style={{ marginTop: 8 }}>
              <div style={{ marginTop: 8 }}>
                <Space size={[8, 8]} wrap>
                  {filteredVars.map((v) => (
                    <Tag
                      key={v}
                      style={{ cursor: "pointer" }}
                      onClick={() => insertVar(v)}>
                      {v}
                    </Tag>
                  ))}
                </Space>
              </div>
            </div>
          </FieldRow>
        )}

        {isEVoucher &&
          (selectedCampaignChannel === ChannelEnum.Sms ||
            selectedCampaignChannel === ChannelEnum.WhatsAppStandard ||
            selectedCampaignChannel === ChannelEnum.WhatsAppPremium) && (
            <>
              <FieldRow
                label={
                  selectedCampaignChannel === ChannelEnum.WhatsAppStandard
                    ? "OK Auto-Reply Message"
                    : "Message CTA"
                }>
                <Radio.Group
                  value={evoucherCustom ? 2 : 1}
                  onChange={(e) => {
                    const isCustom = e.target.value === 2;
                    setEvoucherCustom(isCustom);
                    const name = form.getFieldValue("Name")?.trim();
                    if (
                      name &&
                      campaignInfo?.MemberPortalUrl &&
                      selectedCampaignChannel
                    ) {
                      if (isCustom) {
                        const customUrl = form
                          .getFieldValue("CustomUrl")
                          ?.trim();
                        fetchShortenLink(
                          `${campaignInfo.MemberPortalUrl}?utm_source=${ChannelKeyMap[selectedCampaignChannel]}&utm_campaign=${name}&utm_medium=MemberPortal${customUrl ? `&targetedUrl=${encodeURIComponent(customUrl)}` : ""}`,
                        );
                      } else {
                        fetchShortenLink(
                          `${campaignInfo.MemberPortalUrl}?utm_source=${ChannelKeyMap[selectedCampaignChannel]}&utm_campaign=${name}&utm_medium=MemberPortal`,
                        );
                      }
                    }
                  }}>
                  <Space direction="vertical">
                    <Radio value={1}>
                      <Text style={{ fontWeight: "normal" }}>
                        {selectedCampaignChannel ===
                        ChannelEnum.WhatsAppStandard
                          ? "Default Auto-Reply"
                          : "Use default CTA"}
                      </Text>
                      <div style={{ color: "grey", fontWeight: "normal" }}>
                        {selectedCampaignChannel ===
                        ChannelEnum.WhatsAppStandard
                          ? "Use the default auto-reply message and reward link sent after customers reply with “OK”."
                          : "Use the default CTA label and destination link."}
                      </div>
                    </Radio>
                    <Radio value={2}>
                      <Text style={{ fontWeight: "normal" }}>
                        {selectedCampaignChannel ===
                        ChannelEnum.WhatsAppStandard
                          ? "Customize Auto-Reply"
                          : "Customize CTA"}
                      </Text>
                      <div style={{ color: "grey", fontWeight: "normal" }}>
                        {selectedCampaignChannel ===
                        ChannelEnum.WhatsAppStandard
                          ? "Customize the auto-reply message and destination link sent after customers reply with “OK”."
                          : "Customize the CTA label and destination link."}{" "}
                      </div>
                    </Radio>
                  </Space>
                </Radio.Group>
              </FieldRow>
              {evoucherCustom && (
                <>
                  <FieldRow
                    label={
                      selectedCampaignChannel === ChannelEnum.WhatsAppStandard
                        ? "Reply CTA Text"
                        : "CTA Text"
                    }
                    required>
                    <Form.Item
                      name="CustomCopyText"
                      style={{ marginBottom: 0 }}
                      rules={[
                        { required: true, message: "Please input CTA Text" },
                      ]}>
                      <Input placeholder='e.g. "View here"' />
                    </Form.Item>
                    <div style={{ color: "#8c8c8c", marginTop: 6 }}>
                      {selectedCampaignChannel === ChannelEnum.WhatsAppStandard
                        ? "Displayed before the link in the WhatsApp auto-reply message."
                        : "This text will appear before the destination link in the message preview."}
                    </div>
                  </FieldRow>
                  <FieldRow
                    label={
                      selectedCampaignChannel === ChannelEnum.WhatsAppStandard
                        ? "Reply Destination Link"
                        : "CTA Destination Link"
                    }
                    required>
                    <Form.Item
                      name="CustomUrl"
                      style={{ marginBottom: 0 }}
                      rules={[
                        { type: "url", message: "Please enter a valid URL" },
                        {
                          required: true,
                          message: "Please input CTA Destination Link",
                        },
                      ]}>
                      <Input
                        prefix={<LinkOutlined />}
                        placeholder="https://..."
                        onChange={(e) => {
                          const customUrl = e.target.value.trim();
                          const campaignName = form
                            .getFieldValue("Name")
                            ?.trim();
                          if (!customUrl || !campaignName) return;
                          if (shortenLinkTimerRef.current)
                            clearTimeout(shortenLinkTimerRef.current);
                          shortenLinkTimerRef.current = setTimeout(() => {
                            const generatedLink = `${campaignInfo?.MemberPortalUrl}?utm_source=${
                              selectedCampaignChannel
                                ? ChannelKeyMap[selectedCampaignChannel]
                                : ""
                            }&utm_campaign=${campaignName}&utm_medium=MemberPortal&targetedUrl=${encodeURIComponent(customUrl)}`;
                            fetchShortenLink(generatedLink);
                          }, 800);
                        }}
                      />
                    </Form.Item>
                    <div style={{ color: "#8c8c8c", marginTop: 6 }}>
                      {selectedCampaignChannel === ChannelEnum.WhatsAppStandard
                        ? "This link will be included in the WhatsApp auto-reply message."
                        : "Customers will be redirected to this link when clicking the CTA."}
                    </div>
                  </FieldRow>
                </>
              )}
              {showLink && link && (
                <FieldRow label={<span>Campaign Tracking Link (CTA)</span>}>
                  <Form.Item
                    name="Link"
                    label=""
                    rules={[
                      { required: false },
                      { type: "url", message: "Please enter a valid URL" },
                    ]}>
                    <Input
                      prefix={<LinkOutlined />}
                      placeholder="Enter a Call to Action Link"
                      value={link}
                      disabled={true}
                    />
                    <div style={{ color: "#8c8c8c", marginTop: 6 }}>
                      This link is automatically generated for your campaign
                      tracking. Allowing you to monitor customer engagement and
                      clicks.{" "}
                    </div>
                  </Form.Item>
                </FieldRow>
              )}
            </>
          )}

        <FieldRow
          label={
            <span>
              Preview Message{" "}
              <Tooltip
                title={
                  <>
                    This preview message shows the content message
                    <br /> along with the Campaign Tracking Link (CTA),
                    <br /> While the Unsubscribe link displayed is only a
                    placeholder.
                  </>
                }
                placement="right">
                <InfoCircleOutlined
                  style={{ color: "#8c8c8c", cursor: "pointer" }}
                />
              </Tooltip>
            </span>
          }>
          {/* Preview */}
          <div>
            <Text strong>Live Preview</Text>
            <Card size="small" style={{ marginTop: 8 }}>
              <div style={{ color: "#8c8c8c" }}>
                {previewUrl && (
                  <>
                    <AntdImage
                      key={previewUrl}
                      src={previewUrl}
                      alt="Campaign preview"
                      width={250}
                      height={150}
                      style={{
                        objectFit: "contain",
                        borderRadius: 8,
                        border: "1px solid #f0f0f0",
                      }}
                      preview={false}
                    />
                    <br />
                  </>
                )}
                {selectedCampaignChannel === ChannelEnum.Sms && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: finalMessage
                        ? DOMPurify.sanitize(
                            finalMessage.replace(/\n/g, "<br/>"),
                          )
                        : "Your message will appear here...",
                    }}
                  />
                )}
                {selectedCampaignChannel === ChannelEnum.WhatsAppPremium && (
                  <>
                    <div style={{ fontSize: 14 }}>
                      {" "}
                      {selectedMessageTemplates
                        ? renderMessage(selectedMessageTemplates.Content)
                        : "Your message templates will appear here..."}
                    </div>

                    <Space
                      direction="vertical"
                      style={{ width: "100%", marginTop: 16 }}>
                      {selectedMessageTemplates?.TextLink1 && (
                        <Button
                          block
                          style={{
                            color: "#00A884",
                            fontWeight: 600,
                          }}
                          icon={
                            <img
                              src="/icon/icon-button-whatsapp.svg"
                              style={{ width: 16 }}
                            />
                          }>
                          {selectedMessageTemplates?.TextLink1}
                        </Button>
                      )}

                      {selectedMessageTemplates?.TextLink2 && (
                        <Button
                          block
                          style={{
                            color: "#00A884",
                            fontWeight: 600,
                          }}
                          icon={
                            <img
                              src="/icon/icon-button-whatsapp.svg"
                              style={{ width: 16 }}
                            />
                          }>
                          {selectedMessageTemplates?.TextLink2}
                        </Button>
                      )}
                    </Space>
                  </>
                )}
                {selectedCampaignChannel === ChannelEnum.WhatsAppStandard && (
                  <div
                    className="whatsapp-preview"
                    dangerouslySetInnerHTML={{
                      __html: !isEmptyHtml(msg)
                        ? DOMPurify.sanitize(msg!)
                        : "Your Message will appear here...",
                    }}
                  />
                )}
                {selectedCampaignChannel === ChannelEnum.WhatsAppStandard && (
                  <>
                    <br />
                    👉 Reply with <b>OK</b> to receive your membership login
                    link
                    <br />
                    👉 Reply with <b>STOP</b> to opt out of future WhatsApp
                    messages
                  </>
                )}{" "}
                {selectedCampaignChannel === ChannelEnum.Email &&
                  (composedEmailPreview ? (
                    <iframe
                      key={composedEmailPreview}
                      srcDoc={composedEmailPreview}
                      title="Email Live Preview"
                      style={{
                        width: "calc(100% + 24px)",
                        margin: "-12px",
                        border: "none",
                        display: "block",
                        minHeight: 400,
                      }}
                      onLoad={(e) => {
                        try {
                          const h =
                            e.currentTarget.contentDocument?.body?.scrollHeight;
                          if (h) e.currentTarget.style.height = `${h}px`;
                        } catch {}
                      }}
                    />
                  ) : (
                    <span>Select a campaign type to preview the email.</span>
                  ))}
              </div>
            </Card>
            <Row justify="space-between" style={{ marginTop: 6 }}>
              <Col>
                <Text type="secondary">
                  {selectedCampaignChannel === ChannelEnum.Sms && (
                    <Text type="secondary">Characters used: {charCount}</Text>
                  )}
                  {/* ( {MAX_LENGTH} per SMS ) */}
                </Text>
              </Col>
              <Col>
                {selectedCampaignChannel === ChannelEnum.Sms && (
                  <Text type="secondary">Total SMS: {smsSegments}</Text>
                )}
              </Col>
            </Row>
          </div>
        </FieldRow>
        {/* OK Auto-Reply Preview — commented out
        {selectedCampaignChannel === ChannelEnum.WhatsAppStandard &&
          isEVoucher &&
          evoucherCustom && (
            <FieldRow label="OK Auto-Reply Preview">
              <Card size="small" style={{ marginTop: 8 }}>
                <div style={{ color: "#8c8c8c", whiteSpace: "pre-line" }}>
                  <div>
                    {evoucherCTAText || "Explore and purchase e-vouchers here:"}
                  </div>
                  <div>👉 {link || "{{memberPortalLink}}"}</div>
                </div>
              </Card>
            </FieldRow>
          )}
        */}
        {selectedCampaignChannel === ChannelEnum.Sms && (
          <Alert
            message={<strong> Important Notice</strong>}
            description={
              <>
                Some messages may use extra SMS when variables are applied. This
                can affect total recipients covered by your budget.
              </>
            }
            type="warning"
            showIcon
          />
        )}

        {/* Bottom actions */}
        {!isMobileOrTablet ? (
          <Row justify="end" gutter={8} style={{ marginTop: 16 }}>
            <Col>
              <Button
                loading={loading || loadingRefreshMember}
                onClick={async () => {
                  try {
                    const values = await form.validateFields();
                    await handleSubmit({ ...values, Status: StatusEnum.Draft });
                  } catch (error) {
                    handleError(error);
                  }
                }}>
                Save as Draft
              </Button>
            </Col>
            <Col>
              <Button
                loading={loading || loadingRefreshMember}
                type="primary"
                onClick={async () => {
                  try {
                    const values = await form.validateFields();
                    await handleSubmit({
                      ...values,
                      Status: StatusEnum.Active,
                    });
                  } catch (error) {
                    handleError(error);
                  }
                }}>
                Submit
              </Button>
            </Col>
          </Row>
        ) : (
          <Row justify="end" gutter={8} style={{ marginTop: 16 }}>
            <Col xs={12}>
              <Button
                className="w-100"
                loading={loading || loadingRefreshMember}
                onClick={async () => {
                  const values = await form.validateFields();
                  await handleSubmit({ ...values, Status: StatusEnum.Draft });
                }}>
                Save as Draft
              </Button>
            </Col>
            <Col xs={12}>
              <Button
                className="w-100"
                type="primary"
                loading={loading || loadingRefreshMember}
                onClick={async () => {
                  try {
                    const values = await form.validateFields();
                    await handleSubmit({
                      ...values,
                      Status: StatusEnum.Active,
                    });
                  } catch (error) {
                    handleError(error);
                  }
                }}>
                Submit
              </Button>
            </Col>
          </Row>
        )}
      </Card>
      <Modal
        title={
          selectedType === CampaignTypeEnum.EVoucher
            ? "Select E-Voucher(s)"
            : "Select Voucher(s)"
        }
        open={voucherOpen}
        onCancel={() => {
          setSelectedKey(null);
          setSelectedVoucher(null);
          setVoucherOpen(false);
          setSelectedEVoucher(null);
        }}
        footer={null}
        maskClosable={false}
        width={1200}>
        <Row justify={"space-between"}>
          <Col span={12}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder={`Search by ${
                selectedType === CampaignTypeEnum.EVoucher ? "E-" : ""
              }Voucher's name`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
          <Col span={12} style={{ textAlign: "end" }}>
            <a
              href={`${
                selectedType === CampaignTypeEnum.EVoucher
                  ? process.env.REACT_APP_DASHBOARD_GETZPASS_URL
                  : process.env.REACT_APP_DASHBOARD_URL
              }${
                selectedType === CampaignTypeEnum.EVoucher
                  ? "/Product#/e-voucher/create/0"
                  : "/voucher/create"
              }`}
              target="_blank"
              rel="noopener noreferrer">
              <Button type="primary">
                {" "}
                <PlusOutlined />{" "}
                {selectedType === CampaignTypeEnum.EVoucher
                  ? "Create E-Voucher"
                  : "Create Voucher"}
              </Button>
            </a>
          </Col>
        </Row>

        <Space direction="vertical" style={{ width: "100%" }}>
          <VoucherTable
            selectedType={selectedType}
            voucherData={filteredVouchers}
            eVoucherData={filteredVEouchers}
            selectedKey={selectedKey}
            onChangeSelected={(key, row) => {
              setSelectedKey(key);
              if (
                selectedType === CampaignTypeEnum.Voucher ||
                selectedType === CampaignTypeEnum.VoucherPoint
              ) {
                setSelectedVoucher(row);
              }
              if (selectedType === CampaignTypeEnum.EVoucher) {
                setSelectedEVoucher(row);
              }
            }}
            onAddSelect={(row) => {
              setSelectedKey(row.Id);
              if (
                selectedType === CampaignTypeEnum.Voucher ||
                selectedType === CampaignTypeEnum.VoucherPoint
              ) {
                setSelectedVoucher(row);
              }
              if (selectedType === CampaignTypeEnum.EVoucher) {
                setSelectedEVoucher(row);
              }
              setVoucherOpen(false);
            }}
            loading={loadingVoucher}
          />
        </Space>
      </Modal>
      {/* Billing Modal */}
      <BillingModal
        open={isBillingModalOpen}
        campaignName={campaignName}
        channel={selectedCampaignChannel}
        estimatedMembers={estimatedTotalMember}
        costPerMessage={cost}
        smsSegments={smsSegments}
        maxBudget={pendingPayload?.Budget}
        mode={pendingPayload?.Mode}
        campaignStart={pendingPayload?.CampaignStart}
        campaignEnd={pendingPayload?.CampaignEnd}
        confirmLabel="Submit Campaign"
        skipBilling={true}
        onClose={() => setIsBillingModalOpen(false)}
        onPaymentLater={onPaymentlater}
        onConfirmPayment={async (email) => {
          try {
            setLoading(true);
            const resultId = await campaignService.createCampaignDetails(
              { ...pendingPayload!, PaymentAccountEmail: email },
              excludeRawFile ?? undefined,
            );
            if (!resultId) throw new Error("Failed to create campaign");
            setNewCampaignId(resultId);
          } catch {
            console.error("Error creating campaign");
          } finally {
            setLoading(false);
          }
        }}
        onSuccess={() => {
          setIsBillingModalOpen(false);
          setIsCampaignSuccessModalOpen(true);
        }}
        onFailed={() => {
          setIsBillingModalOpen(false);
          setIsCampaignFailedModalOpen(true);
        }}
        hidePayLater={true}
      />

      {/* Campaign Success Modal */}
      <Modal
        open={isCampaignSuccessModalOpen}
        onCancel={() => setIsCampaignSuccessModalOpen(false)}
        footer={null}
        centered
        width={520}
        closable={false}
        maskClosable={false}
        styles={{ body: { paddingTop: 28, paddingBottom: 24 } }}>
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", textAlign: "center" }}
          size={16}>
          <CheckCircleFilled style={{ fontSize: 80, color: "#2ecc71" }} />
          <Title level={4} style={{ margin: 0 }}>
            Campaign Created Successfully
          </Title>
          <Text type="secondary">
            Your campaign <b>{campaignName}</b> has been created successfully.
          </Text>
        </Space>
        <Divider style={{ margin: "16px 0 8px" }} />
        <Button
          type="primary"
          size="large"
          className="w-100"
          onClick={() => {
            setIsCampaignSuccessModalOpen(false);
            navigate(
              `${RoutePaths.IFRAME_DETAIL_CAMPAIGN}${location.search}&id=${newCampaignId}`,
            );
          }}
          style={{ marginTop: 8 }}>
          View Details
        </Button>
      </Modal>

      {/* Campaign Failed Modal */}
      <Modal
        open={isCampaignFailedModalOpen}
        onCancel={() => setIsCampaignFailedModalOpen(false)}
        footer={null}
        centered
        width={520}
        closable={false}
        maskClosable={false}
        styles={{ body: { paddingTop: 28, paddingBottom: 24 } }}>
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", textAlign: "center" }}
          size={16}>
          <ExclamationCircleFilled style={{ fontSize: 80, color: "#ff4d4f" }} />
          <Title level={4} style={{ margin: 0 }}>
            Campaign Creation Failed
          </Title>
          <Text type="secondary">
            Your campaign <b>{campaignName}</b> could not be created. Please try
            again.
          </Text>
        </Space>
        <Divider style={{ margin: "16px 0 8px" }} />
        <Button
          size="large"
          className="w-100"
          onClick={() => setIsCampaignFailedModalOpen(false)}
          style={{ marginTop: 8 }}>
          Close
        </Button>
      </Modal>

      <Modal
        open={isModalWarningCreateCampaignVisible}
        onCancel={() => setIsModalWarningCreateCampaignVisible(false)}
        footer={null}
        centered
        width={620}
        closeIcon={<CloseOutlined />}
        styles={{ body: { paddingTop: 28, paddingBottom: 16 } }}>
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%" }}
          size={16}>
          <ExclamationCircleFilled style={{ fontSize: 80, color: "#EEB711" }} />
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            Send with Partial Reach
          </Title>
          <Text type="secondary" style={{ textAlign: "center" }}>
            Your budget will only cover <b>{maxReach}</b> of{" "}
            <b>{estimatedTotalMember}</b> targeted members. Do you want to
            continue?
          </Text>

          <Space style={{ marginTop: 12 }}>
            <Button
              size="large"
              onClick={() => setIsModalWarningCreateCampaignVisible(false)}>
              Go Back
            </Button>
            <Button
              type="primary"
              size="large"
              // onClick={() => handleSaveCampaign()}
            >
              Send Anyway
            </Button>
          </Space>
        </Space>
      </Modal>
      <MessageTemplatesModal
        campaignType={selectedType}
        open={messageTemplatesOpen}
        templates={listMessageTemplates}
        onCancel={() => setMessageTemplatesOpen(false)}
        onSelect={(template) => {
          setSelectedMessageTemplates(template);
          setMessageTemplatesOpen(false);
        }}
      />
      <RFMGeneratingModal
        visible={loading || loadingRefreshMember}
        message="Preparing Campaign Data..."
      />
      <PreviewMemberModal
        open={isPreviewOpen}
        onClose={closePreview}
        estimatedMember={estimatedMember}
      />
      <ExcludeMemberPreviewModal
        open={isExcludePreviewOpen}
        onClose={() => setIsExcludePreviewOpen(false)}
        rows={excludeRows}
      />
      <Modal
        open={emailPreviewOpen}
        onCancel={() => {
          setEmailPreviewOpen(false);
          setEmailPreviewHtml(null);
        }}
        footer={null}
        centered
        width={820}
        title="Email Template Preview"
        styles={{ body: { padding: 0, minHeight: 200 } }}>
        {emailPreviewLoading ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: 60,
              gap: 12,
            }}>
            <SyncOutlined spin style={{ fontSize: 32, color: "#1677ff" }} />
            <div style={{ color: "#888" }}>Loading preview...</div>
          </div>
        ) : emailPreviewHtml ? (
          <iframe
            srcDoc={emailPreviewHtml}
            title="Email Preview"
            style={{
              width: "100%",
              height: 640,
              border: "none",
              display: "block",
            }}
          />
        ) : (
          <div style={{ textAlign: "center", padding: 40, color: "#888" }}>
            No preview available.
          </div>
        )}
      </Modal>
    </Form>
  );
};

export default CreateCampaign;
